/**
 * Automatic Line Mode
 *
 * Detects lines in the scene, allows user interaction with them,
 * and creates shapes/rooms from line intersections.
 *
 * Features:
 * - Automatic line detection on camera movement
 * - Interactive line highlighting and selection
 * - Shape creation from line intersections
 * - Room conversion from completed shapes
 */

import { Viewer } from '../../../../viewer/viewer';
import { IPersona } from '../../../types';
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineDetectionService } from '../../modules/line-detection/LineDetectionService';
import { LineRenderer } from '../../modules/line-detection/LineRenderer';
import { ShapeBuilder } from '../../modules/line-detection/ShapeBuilder';
import { DetectedLine, LineShape, LineDetectionConfig, LinePrompt, PromptConnection } from '../../modules/line-detection/types';
import { applyConstraintSnap } from '../../modules/line-detection/ConstraintSnapHook';
import { CoordinateTransformer } from '../../modules/utils/CoordinateTransformer';
import { LineSelectionHitTester } from '../../modules/line-detection/LineSelectionHitTester';
import { LinePromptVisualizer } from '../../modules/line-detection/LinePromptVisualizer';
import { PromptSnapshot, LineToolContext } from './types';
import { PromptAligner } from '../../modules/line-detection/PromptAligner';
import { DensitySnapper } from '../../modules/line-detection/DensitySnapper';
import { BoxExtrusionHandler } from '../../modules/line-detection/BoxExtrusionHandler';
import { ShapeRenderer as LineShapeRenderer } from '../../modules/line-detection/ShapeRenderer';
import { ConstraintSnapVisuals } from '../../modules/line-detection/ConstraintSnapVisuals';
import { EdgeOpController } from '../../modules/line-detection/EdgeOpController';
import { RegionBrushManager } from '../../modules/line-detection/RegionBrushManager';
import { PromptDragHandler, PromptSphereDragState } from '../../modules/line-detection/PromptDragHandler';
import { PromptDeletionManager } from '../../modules/line-detection/PromptDeletionManager';
import { LineDetectionDebugWidget } from '../../widgets/LineDetectionDebugWidget';
import { ClipTask } from '../../../../defines';
import { PointoramaPlane } from '../../../../utils/PointoramaPlane';
import type { EditableShape } from './EditableShape';
import { BasePlaneState } from './SelectionContextProvider';
import { AreaSelector } from '../../modules/selection/AreaSelector';
import { ToolSettingsChangeEvent } from '../../../ToolSettings';
import { ImageCapture } from '../../modules/capture/ImageCapture';
import { PolygonRenderer3D } from '../../modules/segmentation/PolygonRenderer3D';
import { SparkleParticleSystem } from '../../modules/segmentation/SparkleParticleSystem';
import type { DepthAutoPrompt, RegionGraph, ProcessedRegionPolygon, PreviewRectResult } from '../../modules/line-detection/LineDetectionWorkerManager';
import { createPolygon } from '../../../../utils/segmentation/types';
import { PointCloudOctree } from '../../../../PointCloudOctree';

export class AutomaticLineMode {
  protected viewer: Viewer;
  protected persona: IPersona;
  private parentTool: LineToolContext;

  // Base-plane state (passed from parent LineTool)
  protected currentBasePlane: BasePlaneState | null = null;

  // Core modules
  private lineDetectionService: LineDetectionService;
  private lineRenderer: LineRenderer;
  private shapeBuilder: ShapeBuilder;
  private coordinateTransformer: CoordinateTransformer;
  private areaSelector: AreaSelector;
  private hitTester: LineSelectionHitTester;
  private visualizer: LinePromptVisualizer;

  // Extracted modules
  private promptAligner: PromptAligner;
  private densitySnapper: DensitySnapper;
  private boxExtrusionHandler: BoxExtrusionHandler;
  private shapeRendererModule: LineShapeRenderer;
  private regionBrushManager: RegionBrushManager;
  private promptDragHandler: PromptDragHandler;
  private deletionManager: PromptDeletionManager;
  /** Hover-highlight regions snapshotted at brush-drag mousedown; folded into
   * the selection once the gesture passes the drag threshold, so painting
   * EXTENDS the smartly collected highlight instead of restarting from bare
   * brush rects. Null when nothing is pending. */
  private pendingBrushSeedRegionIds: number[] | null = null;
  /** Shift-while-brushing axis lock: anchored where Shift engaged, latched to
   * the dominant SCREEN axis once the cursor commits to a direction (so the
   * stroke can't flip axes when crossing the diagonal). Null while unlocked. */
  private brushAxisLock: { anchor: THREE.Vector2; axis: 'x' | 'y' | null } | null = null;
  /** Axis of the previous Shift engagement in this stroke — re-holding Shift
   * rotates the lock 90° from it instead of re-latching by movement. */
  private lastBrushAxis: 'x' | 'y' | null = null;

  // Shared infrastructure (aliases to LineTool-owned objects via LineToolContext)
  private planePreviewScene: THREE.Scene;
  private mouse: THREE.Vector2;

  // Debug widget
  private debugWidget: LineDetectionDebugWidget | null = null;

  // State
  private detectedLines: DetectedLine[] = [];
  private linePrompts: LinePrompt[] = []; // Persistent prompts with world position and direction
  private nextPromptId = 0;

  // Shape state (rendering delegated to shapeRendererModule)
  /**
   * True between activate() and deactivate(). Async chains
   * (runSegmentation().then(() => detectLines())) can settle after teardown;
   * detectLines() gates on this so no fresh detection starts post-deactivate.
   */
  private modeActive = false;
  private isDetecting = false;
  private lastDetectionTime = 0;
  private detectionDebounceMs = 150; // Wait after camera stops moving before running detection

  // Two-click line creation state
  private firstClickPoint: THREE.Vector3 | null = null; // First point for manual line creation

  // Prompt hover state
  private hoveredPromptId: string | null = null; // ID of currently hovered prompt

  // Preview line hover state (for detected lines before clicking)
  private hoveredPreviewLineId: string | null = null; // ID of currently hovered preview line (sphere or line)

  // Shift key state for showing all preview lines
  private shiftDown = false;

  // Control key state for line mode toggle / other ctrl behaviors
  private ctrlDown = false;
  // Perpendicular snap state (activated by Shift when prompts exist)
  private perpendicularSnapTarget: {
    prompt: LinePrompt;
    snapPoint: THREE.Vector3; // Point on the prompt line closest to cursor
    previewEndPoint: THREE.Vector3; // Where the preview line should end (at cursor)
    distance: number; // Distance from cursor to the prompt line
  } | null = null;
  // Locked prompt ID for shift-hover snap targeting
  private shiftLockedPromptId: string | null = null;

  // Drag box selection for aligning prompts
  private dragBoxStart: THREE.Vector2 | null = null;
  // Right-drag box for deleting prompts
  private rightDragStart: THREE.Vector2 | null = null;
  private rightDragWasBox = false; // True if right-drag opened a selection box
  private dragStartWorldPoint: THREE.Vector3 | null = null; // World position at drag start for line creation
  private dragStartedOnDetectedLine = false; // Track if drag started on a detected line

  // Drag from prompt line to create a box (extrusion)
  private promptLineDrag: {
    prompt: LinePrompt; // The prompt being dragged from (may switch during drag when snappedToEndpoint)
    startPoint: THREE.Vector3; // Point on the prompt line where drag started
    side: 'positive' | 'negative'; // Which side of the prompt the drag started from
    snappedToEndpoint?: boolean; // True when drag started from an endpoint/intersection
    connectedPrompt?: LinePrompt; // The other prompt at the intersection (for edge switching)
  } | null = null;

  // Density-snapped box edges computed during prompt line drag preview
  private snappedBoxEdges: Array<[THREE.Vector3, THREE.Vector3]> | null = null;

  // Hint override tracking (avoids redundant push/pop on every mousemove)
  private hoverOverrideId: string | null = null;

  // Drag a prompt sphere to reposition it with pixel alignment
  private promptSphereDrag: PromptSphereDragState | null = null;

  // Last mouse position during prompt sphere drag (for re-running on shift toggle)
  private lastDragMouse: THREE.Vector2 | null = null;

  // Camera movement tracking
  private cameraMoving = false;
  private cameraMoveTimeout: number | null = null;
  private lastCameraPosition: THREE.Vector3 | null = null;
  private lastCameraTarget: THREE.Vector3 | null = null;
  private lastCameraZoom: number | null = null;

  // Cancellation support for line detection
  private detectionAbortController: AbortController | null = null;

  // Shape edit mode state. Polymorphic across kinds via EditableShape.
  private editingShape: EditableShape | null = null;
  private editingShapeOriginalPolygon: THREE.Vector3[] | null = null;

  // Settings for custom segment isolation
  private settings = {
    enableCustomSegmentIsolation: false,
    segmentIsolationTolerance: 0.1, // in meters
    segmentIsolationHeight: 1.2, // in meters
    temporaryPointSize: 1.0, // temporary point size override (persists across tool activations)
  };

  // Store original point sizes for restoration when tool exits
  private originalPointSizes: Map<PointCloudOctree, number> = new Map();

  // Track if point size has been initialized from point cloud (only do this once)
  private pointSizeInitialized = false;

  // Depth segmentation (position map + hover preview)
  private imageCapture: ImageCapture;
  private polygonRenderer: PolygonRenderer3D;
  private sparkleParticles: SparkleParticleSystem;
  private constraintSnapVisuals: ConstraintSnapVisuals;
  private edgeOpController: EdgeOpController;
  private autoPrompts: DepthAutoPrompt[] = [];
  private regionGraph: RegionGraph | null = null;
  private autoPromptSpheresGroup: THREE.Group | null = null;
  private regionConnectionsGroup: THREE.Group | null = null;
  private hoveredAutoPromptIndex = -1;
  private isPreviewPending = false;
  private lastPreviewPosition: THREE.Vector2 | null = null;
  private queuedPreviewPosition: THREE.Vector2 | null = null; // Position that arrived while preview was pending

  // Region polygon processing cache
  private regionPolygonCache: Map<string, ProcessedRegionPolygon> = new Map();
  private currentHoveredRegionKey: string | null = null;
  private cachedProcessedPolygon: ProcessedRegionPolygon | null = null; // Latest polygon from confirmed mask
  private confirmedPolygonSnapshot: ProcessedRegionPolygon | null = null; // Snapshot taken when user confirms (lifts mouse)
  private lastPreviewRectResult: PreviewRectResult | null = null; // Last circle preview from hover

  // Region selection (confirmed regions added via click/drag)

  // Temporary prompt mode state (for extending existing shape)
  // Temporary prompts are stored in linePrompts with isTemporary=true
  private temporaryShapeBuilder: ShapeBuilder;
  private temporaryShapeRenderer: LineShapeRenderer;
  private cutoffPreviewGroup: THREE.Group | null = null;

  // Step timings for debug widget
  private lastStepTimings: {
    capturePositionMap?: number;
    captureDensityMap?: number;
    segmentation?: number;
    lineDetection?: number;
    renderRegionGraph?: number;
    renderLines?: number;
    updateDebugWidget?: number;
    polygonProcessing?: number;
    total?: number;
  } = {};

  // Map resolutions for debug widget
  private lastMapResolutions: {
    positionMap?: { width: number; height: number };
    densityMap?: { width: number; height: number };
  } = {};

  constructor(viewer: Viewer, persona: IPersona, config: LineDetectionConfig | undefined, parentTool: LineToolContext) {
    this.viewer = viewer;
    this.persona = persona;
    this.parentTool = parentTool;

    // Aliases to shared infrastructure from LineTool (LineToolContext)
    this.coordinateTransformer = parentTool.coordinateTransformer;
    this.planePreviewScene = parentTool.planePreviewScene;
    this.hitTester = parentTool.hitTester;
    this.visualizer = parentTool.visualizer;
    this.mouse = parentTool.mouse;

    // Initialize capture and services
    this.imageCapture = new ImageCapture(viewer, this.coordinateTransformer);
    this.lineDetectionService = new LineDetectionService(
      viewer,
      this.coordinateTransformer,
      config,
      this.imageCapture,
      parentTool.kindAdapter.polygonPipelineConfigPath ?? undefined,
      parentTool.kindAdapter.depthPipelineConfigPath ?? undefined,
    );
    this.lineDetectionService.excludedWorldPolygonsProvider = () => this.parentTool.kindAdapter.getExcludedWorldPolygons?.(this.viewer, this.editingShape) ?? [];
    this.lineRenderer = new LineRenderer(viewer);
    this.shapeBuilder = new ShapeBuilder();
    this.shapeBuilder.setCoordinateTransformer(this.coordinateTransformer);

    // Initialize extracted modules
    this.promptAligner = new PromptAligner(this.coordinateTransformer);
    this.densitySnapper = new DensitySnapper(this.coordinateTransformer);
    this.boxExtrusionHandler = new BoxExtrusionHandler(this.coordinateTransformer, this.densitySnapper);
    this.shapeRendererModule = new LineShapeRenderer(viewer, this.planePreviewScene);
    this.regionBrushManager = new RegionBrushManager(viewer, this.coordinateTransformer);
    this.promptDragHandler = new PromptDragHandler(this.coordinateTransformer, this.densitySnapper);
    this.deletionManager = new PromptDeletionManager(
      this.planePreviewScene,
      viewer.renderArea,
      this.visualizer,
      this.promptAligner,
      this.densitySnapper,
      () => ({
        densityMap: this.lineDetectionService.cachedDensityMap,
        positionMap: this.lineDetectionService.cachedPositionMap,
        floorHeight: this.currentBasePlane?.origin.z ?? null,
      }),
      this.coordinateTransformer,
    );

    // Initialize temporary prompt mode modules (for shape extension)
    this.temporaryShapeBuilder = new ShapeBuilder();
    this.temporaryShapeBuilder.setCoordinateTransformer(this.coordinateTransformer);
    this.temporaryShapeRenderer = new LineShapeRenderer(viewer, this.planePreviewScene);

    // Initialize area selector for drag box selection
    this.areaSelector = new AreaSelector(viewer, this.coordinateTransformer, {
      showLabel: true,
      showWarning: false,
      boxStyle: {
        border: '2px dashed white',
        background: 'rgba(100, 100, 100, 0.2)',
      },
    });

    this.customizeAreaSelectorLabel();

    this.polygonRenderer = new PolygonRenderer3D(viewer, this.coordinateTransformer);
    this.sparkleParticles = new SparkleParticleSystem(viewer, this.coordinateTransformer);
    this.constraintSnapVisuals = new ConstraintSnapVisuals(viewer, this.planePreviewScene);
    this.edgeOpController = new EdgeOpController(viewer, {
      getLinePrompts: () => this.linePrompts,
      getHitTester: () => this.hitTester,
      getVisualizer: () => this.visualizer,
      getOverlayScene: () => this.planePreviewScene,
      updateShapePreview: () => this.updateShapePreview(),
      pushHintOverride: (key: string) => this.parentTool.pushHintOverride(key),
      popHintOverride: () => this.parentTool.popHintOverride(),
      isShapeValid: () => {
        const regular = this.linePrompts.filter((p) => !p.isTemporary);
        if (regular.length < 3) return regular.length === this.linePrompts.length;
        const shape = this.shapeBuilder.createShapeFromPrompts(regular);
        return !!shape && shape.vertices.length >= 3 && shape.vertices.length === regular.length;
      },
    });
    this.autoPromptSpheresGroup = new THREE.Group();
    this.autoPromptSpheresGroup.name = 'auto_prompt_spheres';
    this.planePreviewScene.add(this.autoPromptSpheresGroup);
    this.regionConnectionsGroup = new THREE.Group();
    this.regionConnectionsGroup.name = 'region_connections';
    this.planePreviewScene.add(this.regionConnectionsGroup);
  }

  /**
   * Dispatch a view-aware idle phase.
   * In 3D mode dispatches 'idle_3d' so the quest system shows the correct hint.
   */
  private dispatchIdlePhase(): void {
    const is3D = this.viewer.scene.getActiveCamera().type === 'PerspectiveCamera';
    this.parentTool.dispatchToolPhase(is3D ? 'manual_idle' : 'idle');
  }

  activate(): void {
    this.modeActive = true;
    this.parentTool.undoRedo?.clear();

    // Install the base-plane frame provider so every snap-module read
    // re-derives the frame from live state — see ManualLineMode.activate.
    this.installBasePlaneFrameProvider();

    // Set up camera movement listeners
    this.viewer.addEventListener('camera_changed', this.handleCameraChange);
    this.viewer.addEventListener('controls_changed', this.handleCameraChange);

    // Add debug widget to overlay
    if (this.viewer.debugOverlay) {
      const widget = this.getDebugWidget();
      if (widget) {
        this.viewer.debugOverlay.addWidget(widget);
      }
    }

    // Listen for view changes to update projection plane for 3D mode
    this.viewer.personaManager?.viewManager.addEventListener('view_changed', this.handleLineToolViewChanged);

    // Set up 3D mode projection plane if we have a floor
    this.updateProjectionPlane();

    // Initialize point size override from first point cloud and store originals
    this.initializePointSizeOverride();

    // Initial processing - segmentation when no prompts, line detection when prompts exist
    if (this.linePrompts.length > 0) {
      this.detectLines();
    } else {
      this.runSegmentation();
    }

    this.dispatchIdlePhase();
  }

  deactivate(): void {
    this.modeActive = false;
    // Clear any active hint override
    this.parentTool.popHintOverride();
    this.hoverOverrideId = null;

    // Tear down any in-flight edge-op (menu open, target-pick, or input).
    this.edgeOpController.cancel();

    // Remove camera listeners
    this.viewer.removeEventListener('camera_changed', this.handleCameraChange);
    this.viewer.removeEventListener('controls_changed', this.handleCameraChange);
    this.viewer.personaManager?.viewManager.removeEventListener('view_changed', this.handleLineToolViewChanged);

    // Cancel area selection and two-click mode
    this.areaSelector.cancelSelection();
    this.dragBoxStart = null;
    this.rightDragStart = null;
    this.firstClickPoint = null;
    this.promptLineDrag = null;
    this.snappedBoxEdges = null;
    if (this.promptSphereDrag) {
      const { prompt, originalWorldPoint, originalDirection, originalPosLength, originalNegLength, neighborSnapshots } = this.promptSphereDrag;
      prompt.worldPoint.copy(originalWorldPoint);
      prompt.direction.copy(originalDirection);
      prompt.positiveLength = originalPosLength;
      prompt.negativeLength = originalNegLength;
      prompt.screenPosition = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
      for (const snap of neighborSnapshots) {
        snap.prompt.worldPoint.copy(snap.worldPoint);
        snap.prompt.screenPosition.copy(snap.screenPosition);
      }
      this.promptSphereDrag = null;
    }
    // Re-enable orbital controls (in case drag was active)
    this.viewer.controls.enabled = true;
    this.visualizer.clearLinePreview();
    this.visualizer.clearBoxPreview();
    this.constraintSnapVisuals.clear();

    // Clear all prompts on deactivation
    this.linePrompts = [];
    this.temporaryShapeRenderer.clear();
    this.clearCutoffPreview();

    // If editing a room, cancel and restore original
    if (this.editingShape) {
      this.exitShapeEditMode(false);
    }

    // Clear prompt hover state and isolation plane
    this.hoveredPromptId = null;
    this.hoveredPreviewLineId = null;
    this.shiftDown = false;
    this.ctrlDown = false;
    this.parentTool.cancelPlaneRefinement();
    this.perpendicularSnapTarget = null;
    this.shiftLockedPromptId = null;
    this.visualizer.hideIsolationPlane();

    // Plane preview cleanup is handled by LineTool's onDisable

    // Clear projection plane in 3D mode
    this.clearProjectionPlane();

    // Restore original point sizes
    this.restoreOriginalPointSizes();

    // Remove debug widget from overlay and clean up debug visuals
    this.densitySnapper.debugEnabled = false;
    this.densitySnapper.clearDebug(this.viewer.scene.sceneDebug);
    if (this.viewer.debugOverlay && this.debugWidget) {
      try {
        this.viewer.debugOverlay.removeWidget(this.debugWidget.name);
      } catch {
        // Widget already removed
      }
      this.debugWidget = null;
    }

    // Clear detection timeout
    if (this.cameraMoveTimeout !== null) {
      window.clearTimeout(this.cameraMoveTimeout);
      this.cameraMoveTimeout = null;
    }

    // Cancel any ongoing detection by terminating worker
    this.lineDetectionService.cancel();
    if (this.detectionAbortController) {
      this.detectionAbortController.abort();
      this.detectionAbortController = null;
    }

    // Clear rendering
    this.lineRenderer.clear();
    this.clearShape();
    this.visualizer.clearPromptVisuals();

    // Clear depth segmentation and region selection state
    this.autoPrompts = [];
    this.regionGraph = null;
    this.isPreviewPending = false;
    this.lastPreviewPosition = null;
    this.regionBrushManager.regionDragging = false;
    this.pendingBrushSeedRegionIds = null;
    this.brushAxisLock = null;
    this.lastBrushAxis = null;
    this.clearRegionSelection();
    this.regionPolygonCache.clear();
    this.cachedProcessedPolygon = null;
    this.confirmedPolygonSnapshot = null;
    this.polygonRenderer.clearPreviewMesh();
    this.polygonRenderer.clearPolygonRendering();
    this.clearAutoPromptVisuals();
    this.clearBrushCursor();
    this.sparkleParticles.dispose();

    // Dispose visualizer resources
    this.visualizer.dispose();
  }

  /**
   * Handle camera movement - cancel any ongoing detection and start a new one
   */
  private handleCameraChange = (): void => {
    // Check if camera actually moved by comparing position, target, and zoom
    const camera = this.viewer.scene.getActiveCamera();
    const controls = this.viewer.controls;

    const currentPosition = camera.position.clone();
    const currentTarget = (controls as unknown as { getPivot?(): THREE.Vector3 })?.getPivot?.() ?? new THREE.Vector3();
    // For orthographic camera, zoom is determined by frustum size
    const currentZoom = camera.type === 'OrthographicCamera' ? (camera as THREE.OrthographicCamera).right - (camera as THREE.OrthographicCamera).left : (camera as THREE.PerspectiveCamera).zoom;

    // If we have previous values, check if camera actually moved significantly
    // Use a threshold to ignore tiny floating-point drift from camera damping/easing
    const MOVEMENT_THRESHOLD = 1e-6;
    const ZOOM_THRESHOLD = 1e-6;
    if (this.lastCameraPosition && this.lastCameraTarget && this.lastCameraZoom !== null) {
      const positionDelta = currentPosition.distanceToSquared(this.lastCameraPosition);
      const targetDelta = currentTarget.distanceToSquared(this.lastCameraTarget);
      const zoomDelta = Math.abs(currentZoom - this.lastCameraZoom);

      if (positionDelta < MOVEMENT_THRESHOLD && targetDelta < MOVEMENT_THRESHOLD && zoomDelta < ZOOM_THRESHOLD) {
        // Camera drift is below threshold, ignore this event
        return;
      }
    }

    // Update last known position/target/zoom
    this.lastCameraPosition = currentPosition;
    this.lastCameraTarget = currentTarget.clone();
    this.lastCameraZoom = currentZoom;

    this.cameraMoving = true;

    // Cancel any ongoing detection immediately (also clears cachedPositionMap)
    this.lineDetectionService.cancel();
    this.autoPrompts = [];
    this.regionGraph = null;
    this.isPreviewPending = false;
    this.lastPreviewPosition = null;
    this.queuedPreviewPosition = null;
    // Note: We no longer clear polygonRenderer preview mesh or polygon rendering on camera move
    // because they are rendered in world space using their captured world corners
    this.clearAutoPromptVisuals();

    // Clear region polygon cache (becomes invalid after camera movement)
    // But keep cachedProcessedPolygon - it renders in world space with its own corners
    this.regionPolygonCache.clear();
    this.currentHoveredRegionKey = null;
    this.confirmedPolygonSnapshot = null;
    this.pendingPolygonMask = null;

    // Clear selection state (but not the visual rendering which uses world corners)
    this.clearRegionSelection();

    // Note: We no longer clear detected lines or lineRenderer on camera move
    // because they are rendered in world space using their captured world corners
    this.hoveredPreviewLineId = null;
    this.updatePreviewLineHoverState(null);

    // Also abort the controller for any non-worker async operations
    if (this.detectionAbortController) {
      this.detectionAbortController.abort();
      this.detectionAbortController = null;
    }

    // Clear existing timeout
    if (this.cameraMoveTimeout !== null) {
      window.clearTimeout(this.cameraMoveTimeout);
    }

    // Set new timeout to run appropriate processing after camera stops moving
    // Short debounce to avoid overwhelming the system with detections
    this.cameraMoveTimeout = window.setTimeout(() => {
      this.cameraMoving = false;
      this.cameraMoveTimeout = null;

      // Always run line detection after camera stops — this refreshes cached maps.
      // Segmentation: runs when no prompts AND Ctrl not pressed AND not over a room.
      if (this.linePrompts.length > 0) {
        this.detectLines();
      } else if (!this.isMouseOverRoom()) {
        this.runSegmentation();
      }

      // Update shape preview with new lines if we have prompts
      if (this.linePrompts.length >= 3) {
        this.updateShapePreview();
      }
    }, this.detectionDebounceMs);

    // Update prompt screen positions for any hit testing
    this.updatePromptScreenPositions();

    // Update prompt visuals for screen-space dependent sizing in 2D mode
    if (this.linePrompts.length > 0) {
      this.visualizer.updatePromptVisuals(this.linePrompts, this.hoveredPromptId);
    }

    // Lines are now 3D objects in the scene, so they automatically stay in correct position
    // No need to update coordinates - just re-render to update colors/state if needed
    // Use safe render to validate corners before showing
    this.renderDetectedLinesSafe();
  };

  /**
   * Handle view changes - update projection plane and plane preview for 3D mode
   */
  private handleLineToolViewChanged = (): void => {
    this.updateProjectionPlane();

    // Update quest hint based on view mode
    this.dispatchIdlePhase();
  };

  /**
   * Update the projection plane for 3D mode interactions
   * In 3D mode, we need a plane to project clicks onto (uses floor plane at floor height)
   * In 2D mode, the ViewerMode handles this automatically
   */
  private updateProjectionPlane(): void {
    const camera = this.viewer.scene.getActiveCamera();
    const is3DMode = camera.type === 'PerspectiveCamera';

    if (is3DMode && this.currentBasePlane) {
      // In 3D mode, create a projection plane at the base plane for accurate click projection
      const projectionPlane = new PointoramaPlane({
        position: this.currentBasePlane.origin.clone(),
        normal: this.currentBasePlane.normal.clone(),
        clipTask: ClipTask.NONE,
        tolerance: 0.01,
        finished: false,
        selected: false,
      });

      // Set as associated plane for coordinate transformer (for click projection)
      this.viewer.viewerMode.associatedPlane = projectionPlane;
      this.viewer.viewerMode.hasVirtualPlane = true;
    } else {
      // In 2D mode or when no floor is selected, clear our custom projection plane
      this.clearProjectionPlane();
    }
  }

  /**
   * Clear the projection plane set by this tool
   */
  private clearProjectionPlane(): void {
    // Only clear if we set it (check for virtual plane flag)
    if (this.viewer.viewerMode.hasVirtualPlane && !this.viewer.viewerMode.in2DMode) {
      this.viewer.viewerMode.associatedPlane = null;
      this.viewer.viewerMode.hasVirtualPlane = false;
    }
  }

  /**
   * Register the base-plane frame provider — see
   * ManualLineMode.installBasePlaneFrameProvider for the contract.
   */
  protected installBasePlaneFrameProvider(): void {
    this.coordinateTransformer.setBasePlaneFrameProvider(() =>
      this.parentTool.kindAdapter.getBasePlaneFrame({
        viewer: this.viewer,
        basePlane: this.currentBasePlane,
        shape: this.editingShape,
      }),
    );
  }

  /**
   * Update projection plane when the base-plane selection changes
   */
  onBasePlaneChanged(basePlane: BasePlaneState | null): void {
    // `FloorplanToolBase.handleSelectionChanged` re-fires this on every
    // `objects_selection_changed` event (e.g. the door/window auto-commit
    // selecting the new annotation). When the selection didn't actually
    // change (same contextKey), the cache-invalidation + delayed
    // `runSegmentation()` below would force a fresh screenshot after every
    // commit — defeating the multi-shape-in-a-row flow that reuses the
    // existing capture.
    const nextKey = basePlane?.contextKey ?? null;
    if (nextKey === (this.currentBasePlane?.contextKey ?? null)) return;

    this.currentBasePlane = basePlane;
    // Update projection plane when the base plane changes
    this.updateProjectionPlane();
    // No frame resync needed — the provider reads `currentBasePlane` live.

    // Cancel any ongoing detection — cached maps are from the old floor
    this.lineDetectionService.cancel();
    if (this.detectionAbortController) {
      this.detectionAbortController.abort();
      this.detectionAbortController = null;
    }

    // Clear calculated results (detected lines, segmentation, regions)
    this.detectedLines = [];
    this.lineRenderer.clear();
    this.autoPrompts = [];
    this.regionGraph = null;
    this.isPreviewPending = false;
    this.lastPreviewPosition = null;
    this.queuedPreviewPosition = null;
    this.hoveredPreviewLineId = null;
    this.updatePreviewLineHoverState(null);
    this.clearAutoPromptVisuals();
    this.clearRegionSelection();
    this.regionPolygonCache.clear();
    this.currentHoveredRegionKey = null;
    this.cachedProcessedPolygon = null;
    this.confirmedPolygonSnapshot = null;
    this.pendingPolygonMask = null;

    // Clear visual state (screenshots/renders from old floor)
    this.polygonRenderer.clearPreviewMesh();
    this.polygonRenderer.clearPolygonRendering();
    this.clearShape();

    // Note: linePrompts are intentionally preserved across floor changes

    // Clear any pending camera-move timeout to avoid double-detection
    if (this.cameraMoveTimeout !== null) {
      window.clearTimeout(this.cameraMoveTimeout);
      this.cameraMoveTimeout = null;
    }

    // Re-trigger detection/segmentation for the new floor after a delay
    // so the pointcloud has time to load for the new floor
    this.cameraMoveTimeout = window.setTimeout(() => {
      this.cameraMoveTimeout = null;
      if (this.linePrompts.length > 0) {
        this.detectLines();
        if (this.linePrompts.length >= 3) {
          this.updateShapePreview();
        }
      } else {
        this.runSegmentation();
      }
    }, 1000);
  }

  /**
   * Z-range (world height) for the position-map encoding slab.
   *
   * Prefer the detected floor→ceiling extent: that's the actual vertical
   * layer we want the position map (and its depth override) to span, so the
   * full floor survives the per-pixel depth test. The floor-isolation plane's
   * `tolerance` is a thin band (often a few cm) used only to *visually*
   * isolate the floor — using it as the encoding slab clips the floor to that
   * sliver and the "top of the floor" drops out.
   *
   * Falls back to the isolation band when floor/ceiling aren't both known,
   * and to `undefined` (encoder uses cloud bbox) when there's no isolation.
   */
  private getZRange(): [number, number] | undefined {
    const range = this.currentBasePlane?.captureZRange;
    if (!range) return undefined;
    return [Math.min(range[0], range[1]), Math.max(range[0], range[1])];
  }

  /**
   * 8 oriented-box corners for the capture's eye-Z slab — see
   * `AnnotationKindAdapter.getCaptureBoxCorners`. When the adapter
   * doesn't supply one (rooms), returns undefined and the position
   * encoder falls back to the floor-isolation `zRange` + cloud bbox.
   */
  private getCaptureBoxCorners(): THREE.Vector3[] | undefined {
    const corners = this.parentTool.kindAdapter.getCaptureBoxCorners({
      viewer: this.viewer,
      basePlane: this.currentBasePlane,
      shape: this.editingShape,
    });
    return corners ?? undefined;
  }

  /**
   * Run depth segmentation only (no line detection).
   * Called when no line prompts exist and Ctrl is not pressed.
   */
  private async runSegmentation(): Promise<void> {
    // Pure-geometry kinds (outline) opt out of segmentation entirely.
    if (this.parentTool.kindAdapter.enableLineDetection === false) {
      this.polygonRenderer.clearPreviewMesh();
      this.polygonRenderer.clearPolygonRendering();
      this.sparkleParticles.stop();
      return;
    }

    const totalStart = performance.now();
    this.lastStepTimings = {};
    this.lastMapResolutions = {};

    // Clear old preview before starting new segmentation
    // This prevents showing stale masks with wrong corners during the transition
    this.polygonRenderer.clearPreviewMesh();
    this.polygonRenderer.clearPolygonRendering();

    const captureStart = performance.now();
    const captured = await this.lineDetectionService.captureViewportMaps(this.getZRange(), this.getCaptureBoxCorners());
    this.lastStepTimings.capturePositionMap = performance.now() - captureStart;

    if (!captured) return;

    // Record position map resolution
    this.lastMapResolutions.positionMap = { width: captured.positionMap.width, height: captured.positionMap.height };

    // Start sparkle particles to indicate processing
    this.sparkleParticles.start(captured.positionMap);

    try {
      const segmentationStart = performance.now();
      await this.lineDetectionService.runSegmentationOnly();
      this.lastStepTimings.segmentation = performance.now() - segmentationStart;

      // Record density map resolution
      const densityMap = this.lineDetectionService.cachedDensityMap;
      if (densityMap) {
        this.lastMapResolutions.densityMap = { width: densityMap.width, height: densityMap.height };
      }

      // Note: We no longer skip stale results because masks/polygons render in world space
      // using their captured world corners. Even if camera moved during processing,
      // the results will render in the correct world location.

      // Update state from service
      this.autoPrompts = this.lineDetectionService.lastAutoPrompts ?? [];
      this.regionGraph = this.lineDetectionService.lastRegionGraph ?? null;

      // Re-sync selections to workers if we have any (workers reset selection on new depth state)
      // Also immediately render the selection mask and trigger polygon processing
      // so user sees results without needing to move mouse
      if (this.regionBrushManager.selectedRegionIds.size > 0) {
        const selectionResult = await this.lineDetectionService.updateSelectedRegions(Array.from(this.regionBrushManager.selectedRegionIds));
        if (selectionResult.imageData) {
          this.polygonRenderer.renderPreviewMask(selectionResult.imageData, selectionResult.worldCorners);
          // Also process polygon for the selection
          const allIds = Array.from(this.regionBrushManager.selectedRegionIds).sort((a, b) => a - b);
          const cacheKey = `regions_${allIds.join('_')}`;
          this.triggerPolygonProcessing(selectionResult.imageData, cacheKey);
        }
      }

      // Render region graph nodes and connections
      const renderStart = performance.now();
      this.renderRegionGraph();
      this.lastStepTimings.renderRegionGraph = performance.now() - renderStart;

      // Stop sparkle particles after results are rendered and visible
      this.sparkleParticles.stop();

      this.lastStepTimings.total = performance.now() - totalStart;

      // Update debug widget
      const debugStart = performance.now();
      this.updateDebugWidget();
      this.lastStepTimings.updateDebugWidget = performance.now() - debugStart;

      // Refresh the preview at the current mouse position
      this.isPreviewPending = false;
      this.lastPreviewPosition = null;
      this.updatePreview(this.mouse.clone());
    } catch (error) {
      console.error('[LineTool] Segmentation failed:', error);
    }
  }

  /**
   * Detect lines in the current view (line detection only, no segmentation).
   * Called when line prompts exist OR when Ctrl is pressed with no prompts.
   * Uses AbortController to allow cancellation when camera moves.
   */
  private async detectLines(): Promise<void> {
    // Deactivation may land while a chained runSegmentation() is awaiting;
    // don't start a fresh detection (with a fresh, never-aborted controller)
    // against a torn-down mode.
    if (!this.modeActive) return;
    // Pure-geometry kinds (outline) opt out of segmentation entirely.
    if (this.parentTool.kindAdapter.enableLineDetection === false) {
      this.lineRenderer.clear();
      this.detectedLines = [];
      this.sparkleParticles.stop();
      // Detection-less kinds may still need the viewport maps for density
      // snapping while dragging prompts (Wall Tracer room edits).
      if (this.parentTool.kindAdapter.captureMapsForSnapping) {
        await this.lineDetectionService.captureViewportMaps(this.getZRange(), this.getCaptureBoxCorners());
      }
      return;
    }

    const totalStart = performance.now();
    this.lastStepTimings = {};
    this.lastMapResolutions = {};

    // Clear old lines before starting new detection
    // This prevents showing stale lines with wrong corners during the transition
    this.lineRenderer.clear();
    this.detectedLines = [];

    // Cancel any previous detection
    if (this.detectionAbortController) {
      this.detectionAbortController.abort();
    }

    // Create new abort controller for this detection
    this.detectionAbortController = new AbortController();
    const signal = this.detectionAbortController.signal;

    this.isDetecting = true;
    this.lastDetectionTime = Date.now();

    const captureStart = performance.now();
    const captured = await this.lineDetectionService.captureViewportMaps(this.getZRange(), this.getCaptureBoxCorners());
    this.lastStepTimings.capturePositionMap = performance.now() - captureStart;

    // Teardown can land while the capture await is in flight — don't start
    // sparkles or the worker pass against a deactivated mode.
    if (!this.modeActive || signal.aborted) {
      this.isDetecting = false;
      return;
    }

    // Record position map resolution
    if (captured) {
      this.lastMapResolutions.positionMap = { width: captured.positionMap.width, height: captured.positionMap.height };
    }

    // Start sparkle particles to indicate processing
    if (captured) {
      this.sparkleParticles.start(captured.positionMap);
    }

    try {
      const detectionStart = performance.now();
      const result = await this.lineDetectionService.detectLinesOnly(signal);
      this.lastStepTimings.lineDetection = performance.now() - detectionStart;

      // Record density map resolution
      const densityMap = this.lineDetectionService.cachedDensityMap;
      if (densityMap) {
        this.lastMapResolutions.densityMap = { width: densityMap.width, height: densityMap.height };
      }

      // Check if aborted before updating state
      if (signal.aborted) {
        return;
      }

      // Note: We no longer skip stale results because lines render in world space
      // using their captured world corners. Even if camera moved during processing,
      // the results will render in the correct world location.

      this.detectedLines = result.lines;

      // Render detected lines
      const renderStart = performance.now();
      this.lineRenderer.render(this.detectedLines);
      this.lastStepTimings.renderLines = performance.now() - renderStart;

      // Stop sparkle particles after lines are rendered and visible
      this.sparkleParticles.stop();

      this.lastStepTimings.total = performance.now() - totalStart;

      // Update debug widget with detection results
      const debugStart = performance.now();
      this.updateDebugWidget();
      this.lastStepTimings.updateDebugWidget = performance.now() - debugStart;
    } catch (error) {
      // Ignore abort errors - they're expected when camera moves
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      if (error instanceof Error && error.message.includes('aborted')) {
        return;
      }

      console.error('[LineTool] Line detection failed:', error);
    } finally {
      this.isDetecting = false;
      // Update hint after detection completes based on current state. Skip
      // when aborted: this run was superseded or torn down, and dispatching
      // here would re-show the hint after deactivate() already hid it.
      if (!signal.aborted) {
        if (this.editingShape) {
          this.parentTool.dispatchToolPhase('editing_room');
        } else if (this.linePrompts.length > 0) {
          // Don't override — updateShapePreview will dispatch the correct phase
        } else {
          this.dispatchIdlePhase();
        }
      }
    }
  }

  /**
   * Show the depth-segmentation region under the cursor as a green hover highlight.
   * Shows the depth-segmentation regions under the cursor as a combined mask preview.
   * Disabled when editing (linePrompts.length > 0) to avoid distraction.
   * During drag, sampleAndAddRegions handles region addition instead.
   */
  private async updatePreview(screenPos: THREE.Vector2): Promise<void> {
    // Disable preview when editing (have line prompts placed)
    if (this.linePrompts.length > 0) {
      this.polygonRenderer.clearPreviewMesh();
      this.clearRegionPolygonPreview();
      this.clearBrushCursor();
      return;
    }

    // Disable preview when hovering over an existing room
    if (this.isMouseOverRoom()) {
      this.polygonRenderer.clearPreviewMesh();
      this.polygonRenderer.clearPolygonRendering();
      this.clearRegionPolygonPreview();
      this.clearBrushCursor();
      return;
    }

    // Show brush cursor ring
    this.renderBrushCursor(screenPos);

    // If a preview request is already in flight, queue this position for later
    if (this.isPreviewPending) {
      this.queuedPreviewPosition = screenPos.clone();
      return;
    }
    if (this.lastPreviewPosition && screenPos.distanceTo(this.lastPreviewPosition) < 2) return;

    const worldPos = this.coordinateTransformer.screenToWorld(screenPos);
    if (!worldPos) {
      // With selections, keep showing the existing preview
      if (this.regionBrushManager.selectedRegionIds.size === 0) {
        this.polygonRenderer.clearPreviewMesh();
        this.polygonRenderer.clearPolygonRendering();
        this.clearRegionPolygonPreview();
      }
      return;
    }

    try {
      const maskPos = this.coordinateTransformer.worldToMaskSpace(worldPos);
      if (maskPos.x < 0 || maskPos.x > 1 || maskPos.y < 0 || maskPos.y > 1) {
        // With selections, keep showing the existing preview
        if (this.regionBrushManager.selectedRegionIds.size === 0) {
          this.polygonRenderer.clearPreviewMesh();
          this.polygonRenderer.clearPolygonRendering();
          this.clearRegionPolygonPreview();
        }
        return;
      }

      this.isPreviewPending = true;

      // Preview all regions within the brush rectangle (worker combines with confirmed selection)
      const halfSizePixels = this.getBrushHalfSizeInMaskPixels();
      const previewResult = await this.lineDetectionService.getPreviewRect(maskPos.x, maskPos.y, halfSizePixels);
      this.isPreviewPending = false;

      // Check if a new position was queued while we were waiting
      const queuedPos = this.queuedPreviewPosition;
      this.queuedPreviewPosition = null;

      if (!previewResult) {
        if (queuedPos) {
          this.updatePreview(queuedPos);
        }
        // With selections, keep showing the existing preview
        if (this.regionBrushManager.selectedRegionIds.size === 0) {
          this.polygonRenderer.clearPreviewMesh();
          this.polygonRenderer.clearPolygonRendering();
          this.clearRegionPolygonPreview();
        }
        return;
      }

      // Only cache position after successful preview
      this.lastPreviewPosition = screenPos.clone();

      this.polygonRenderer.renderPreviewMask(previewResult.imageData, previewResult.worldCorners);
      this.lastPreviewRectResult = previewResult;

      // Build cache key from hovered + selected region IDs
      const allIds = [...previewResult.regionIds, ...Array.from(this.regionBrushManager.selectedRegionIds)];
      allIds.sort((a, b) => a - b);
      const cacheKey = `regions_${allIds.join('_')}`;

      // No regions at all (empty space, no selection) — clear mask, polygon, and skip processing
      if (allIds.length === 0) {
        this.pendingPolygonMask = null;
        this.polygonRenderer.clearPreviewMesh();
        this.polygonRenderer.clearPolygonRendering();
        this.clearRegionPolygonPreview();
        if (queuedPos && queuedPos.distanceTo(screenPos) >= 2) {
          this.updatePreview(queuedPos);
        }
        return;
      }

      const cachedPolygon = this.regionPolygonCache.get(cacheKey);
      if (cachedPolygon) {
        this.currentHoveredRegionKey = cacheKey;
        this.cachedProcessedPolygon = cachedPolygon;
        this.renderProcessedPolygonPreview(cachedPolygon);
        if (queuedPos && queuedPos.distanceTo(screenPos) >= 2) {
          this.updatePreview(queuedPos);
        }
        return;
      }

      // Always process polygon when the mask has content (new regions or selection)
      this.triggerPolygonProcessing(previewResult.imageData, cacheKey);

      if (queuedPos && queuedPos.distanceTo(screenPos) >= 2) {
        this.updatePreview(queuedPos);
      }
    } catch {
      this.isPreviewPending = false;
      this.queuedPreviewPosition = null;
      if (this.regionBrushManager.selectedRegionIds.size === 0) {
        this.polygonRenderer.clearPreviewMesh();
        this.polygonRenderer.clearPolygonRendering();
        this.clearRegionPolygonPreview();
      }
    }
  }

  /**
   * Convert screen-pixel brush half-size to mask-pixel half-size.
   */
  private getBrushHalfSizeInMaskPixels(): number {
    const viewportWidth = this.viewer.renderArea.clientWidth;
    const maskWidth = this.lineDetectionService.cachedPositionMap?.width ?? viewportWidth;
    return this.regionBrushManager.getBrushHalfSizeInMaskPixels(maskWidth, viewportWidth);
  }

  /**
   * Build a cache key from a set of hovered region IDs + selected region IDs.
   */
  private pendingPolygonMask: { imageData: ImageData; cacheKey: string } | null = null;
  private polygonProcessingActive = false;

  /**
   * Queue a polygon processing update. If already processing, stores the latest
   * mask so it is picked up when the current run finishes. Intermediate states
   * are skipped — only the most recent mask is ever processed.
   */
  private triggerPolygonProcessing(maskImageData: ImageData, cacheKey: string): void {
    this.pendingPolygonMask = { imageData: maskImageData, cacheKey };
    this.drainPolygonQueue();
  }

  /**
   * Self-draining loop: processes the latest pending mask, then checks for
   * newer updates on completion. Skips all intermediate states.
   */
  private async drainPolygonQueue(): Promise<void> {
    if (this.polygonProcessingActive) return; // already running, it will pick up pendingPolygonMask when done
    this.polygonProcessingActive = true;

    try {
      while (this.pendingPolygonMask) {
        // Grab the latest mask and clear the pending slot
        const { imageData, cacheKey } = this.pendingPolygonMask;
        this.pendingPolygonMask = null;

        try {
          const polygonStart = performance.now();
          const processedPolygon = await this.lineDetectionService.processRegionFromMask(imageData.data, imageData.width, imageData.height, -2);
          this.lastStepTimings.polygonProcessing = performance.now() - polygonStart;

          // Always apply the result for immediate visual feedback during drawing
          // Even if a newer mask is pending, show this one now - the loop will
          // continue processing newer masks and update again
          // Skip if line prompts exist (selection was converted to prompts)
          // Skip if preview mask was cleared while processing (user moved to empty space)
          if (processedPolygon && this.linePrompts.length === 0 && this.polygonRenderer.hasPreviewMesh()) {
            this.regionPolygonCache.set(cacheKey, processedPolygon);
            this.currentHoveredRegionKey = cacheKey;
            this.cachedProcessedPolygon = processedPolygon;
            this.renderProcessedPolygonPreview(processedPolygon);
            // Refresh debug panel to show updated polygon pipeline timings
            this.debugWidget?.refreshPipelinePanel();
            // Also update stats to show polygon processing time
            this.updateDebugWidget();
          }
          // If pendingPolygonMask is set, loop continues with the new mask
        } catch (error) {
          if (error instanceof Error && error.name !== 'AbortError') {
            console.warn('[LineTool] Polygon processing failed:', error);
          }
        }
      }
    } finally {
      this.polygonProcessingActive = false;
    }

    // Auto-confirm after all polygon processing is done
    // Use the snapshot that was captured when user lifted mouse, or fall back to cached
    if (this.regionBrushManager.autoConfirmPending && (this.confirmedPolygonSnapshot || this.cachedProcessedPolygon)) {
      this.regionBrushManager.autoConfirmPending = false;
      this.confirmRegionSelection(true);
    }
  }

  /**
   * Render the processed polygon preview like AutoRoomDetectTool.
   * Shows polygon outline with vertices and green fill.
   * If there are selected regions, also includes them in the preview.
   */
  private renderProcessedPolygonPreview(processedPolygon: ProcessedRegionPolygon): void {
    // Note: When selectedRegionIds is non-empty, the worker already combines them with the hover region
    // into a single mask/polygon, so we just render the single processedPolygon which contains the combined result

    // Filter out any points with NaN values
    const validPoints = processedPolygon.points.filter((pt) => Number.isFinite(pt.x) && Number.isFinite(pt.y));

    // Need at least 3 points for a valid polygon
    if (validPoints.length < 3) {
      console.warn('[LineTool] Processed polygon has less than 3 valid points, skipping render');
      return;
    }

    // Convert pixel-space polygon to Polygon type for rendering
    const mainPolygon = createPolygon(
      validPoints.map((pt) => ({ x: pt.x, y: pt.y })),
      false,
    );

    // Add holes as children (also filter NaN values)
    if (processedPolygon.holes && processedPolygon.holes.length > 0) {
      mainPolygon.children = processedPolygon.holes
        .map((hole) => {
          const validHolePoints = hole.filter((pt) => Number.isFinite(pt.x) && Number.isFinite(pt.y));
          if (validHolePoints.length < 3) return null;
          return createPolygon(
            validHolePoints.map((pt) => ({ x: pt.x, y: pt.y })),
            true,
          );
        })
        .filter((h): h is NonNullable<typeof h> => h !== null);
    }

    // Use polygonRenderer to render in 3D (like AutoRoomDetectTool)
    // The renderer expects pixel-space polygons and will convert to world space
    // Pass the stored segmentation corners to ensure correct positioning even if camera moved
    const capturedImageInfo = processedPolygon.imageWidth && processedPolygon.imageHeight ? { canvas: null, width: processedPolygon.imageWidth, height: processedPolygon.imageHeight } : null;
    // Convert worldCorners from {x,y,z} to THREE.Vector3 if present
    const worldCorners = processedPolygon.worldCorners ? processedPolygon.worldCorners.map((c) => new THREE.Vector3(c.x, c.y, c.z)) : null;
    this.polygonRenderer.renderPolygons([mainPolygon], capturedImageInfo, worldCorners);

    // Render pixel alignment debug visualization if available
    // Use the polygon's imageWidth/imageHeight since debug coordinates are in mask space
    const debugData = this.lineDetectionService.lastPixelAlignmentDebug;
    if (debugData && processedPolygon.imageWidth && processedPolygon.imageHeight) {
      const capturedImageInfo = { width: processedPolygon.imageWidth, height: processedPolygon.imageHeight };
      this.polygonRenderer.renderPixelAlignmentDebug(debugData, capturedImageInfo);
    }
  }

  /**
   * Clear the processed polygon preview.
   */
  private clearRegionPolygonPreview(): void {
    this.currentHoveredRegionKey = null;
    this.cachedProcessedPolygon = null;
    this.confirmedPolygonSnapshot = null;
    // Note: polygonRenderer.clearPreviewMesh() is called separately
  }

  /**
   * Render all region graph nodes and their connections.
   * Nodes are rendered as spheres, connections as lines.
   * Only renders if showRegionGraph debug option is enabled in the pipeline.
   */
  private renderRegionGraph(): void {
    if (!this.autoPromptSpheresGroup || !this.regionConnectionsGroup) return;

    // Clear old spheres
    while (this.autoPromptSpheresGroup.children.length > 0) {
      const child = this.autoPromptSpheresGroup.children[0] as THREE.Mesh;
      child.geometry?.dispose();
      (child.material as THREE.Material)?.dispose();
      this.autoPromptSpheresGroup.remove(child);
    }

    // Clear old connections
    while (this.regionConnectionsGroup.children.length > 0) {
      const child = this.regionConnectionsGroup.children[0] as THREE.Line;
      child.geometry?.dispose();
      (child.material as THREE.Material)?.dispose();
      this.regionConnectionsGroup.remove(child);
    }

    // Only render if debug option is enabled
    if (!this.lineDetectionService.lastShowRegionGraph) return;

    if (!this.regionGraph || this.regionGraph.nodes.size === 0) return;

    const radius = this.visualizer.calculateDynamicSphereRadius();
    const geometry = new THREE.SphereGeometry(radius, 12, 8);

    // Map to store world positions by region ID for drawing connections
    const nodeWorldPositions = new Map<number, THREE.Vector3>();

    // Get the min size threshold from the region graph
    const minSize = this.regionGraph.minSize ?? 0;

    // First pass: render all nodes and collect their world positions
    for (const [regionId, node] of this.regionGraph.nodes) {
      // Skip nodes smaller than the threshold
      if (node.size < minSize) continue;

      let worldPos: THREE.Vector3 | null = null;
      try {
        worldPos = this.coordinateTransformer.maskSpaceToWorld(new THREE.Vector2(node.centroidNormalized.x, node.centroidNormalized.y));
      } catch {
        continue;
      }
      if (!worldPos) continue;

      nodeWorldPositions.set(regionId, worldPos.clone());

      // Color: selected nodes are cyan, edge-touching are orange, interior by band (green gradient)
      let color: number;
      if (this.regionBrushManager.selectedRegionIds.has(regionId)) {
        color = 0x00ddff; // Cyan for selected
      } else if (node.touchesEdge) {
        color = 0xff6600; // Orange for edge-touching
      } else {
        // Green gradient based on band (higher band = brighter green)
        const bandIntensity = Math.min(1, node.band / 15); // Normalize to 0-1
        const r = Math.floor(0x00 + (0x00 - 0x00) * bandIntensity);
        const g = Math.floor(0x88 + (0xff - 0x88) * bandIntensity);
        const b = Math.floor(0x44 + (0x88 - 0x44) * bandIntensity);
        color = (r << 16) | (g << 8) | b;
      }

      const material = new THREE.MeshBasicMaterial({
        color,
        depthTest: false,
        transparent: true,
        opacity: 0.85,
      });
      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.copy(worldPos);
      sphere.userData.regionId = regionId;
      sphere.userData.autoPromptIndex = regionId; // For compatibility with existing hover logic
      this.autoPromptSpheresGroup.add(sphere);
    }

    // Second pass: render connections between adjacent nodes
    const drawnConnections = new Set<string>(); // Track drawn connections to avoid duplicates

    for (const [regionId, node] of this.regionGraph.nodes) {
      const fromPos = nodeWorldPositions.get(regionId);
      if (!fromPos) continue;

      for (const edge of node.edges) {
        const toPos = nodeWorldPositions.get(edge.targetRegionId);
        if (!toPos) continue;

        // Create a unique key for this connection (order-independent)
        const connKey = [regionId, edge.targetRegionId].sort((a, b) => a - b).join('-');
        if (drawnConnections.has(connKey)) continue;
        drawnConnections.add(connKey);

        // Create line geometry
        const points = [fromPos, toPos];
        const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);

        // Color based on connectivity strength (more connected = brighter)
        const intensity = Math.min(1, edge.connectivityPercent * 2); // Scale up for visibility
        const lineColor = new THREE.Color().setHSL(0.55, 0.8, 0.3 + intensity * 0.4); // Cyan-ish

        const lineMaterial = new THREE.LineBasicMaterial({
          color: lineColor,
          transparent: true,
          opacity: 0.4 + intensity * 0.4,
          depthTest: false,
        });

        const line = new THREE.Line(lineGeometry, lineMaterial);
        this.regionConnectionsGroup.add(line);
      }
    }
  }

  /**
   * Clear all region graph visuals (nodes and connections).
   */
  private clearAutoPromptVisuals(): void {
    if (this.autoPromptSpheresGroup) {
      while (this.autoPromptSpheresGroup.children.length > 0) {
        const child = this.autoPromptSpheresGroup.children[0] as THREE.Mesh;
        child.geometry?.dispose();
        (child.material as THREE.Material)?.dispose();
        this.autoPromptSpheresGroup.remove(child);
      }
    }
    if (this.regionConnectionsGroup) {
      while (this.regionConnectionsGroup.children.length > 0) {
        const child = this.regionConnectionsGroup.children[0] as THREE.Line;
        child.geometry?.dispose();
        (child.material as THREE.Material)?.dispose();
        this.regionConnectionsGroup.remove(child);
      }
    }
    this.hoveredAutoPromptIndex = -1;
  }

  /**
   * Test whether mouse is over any auto-prompt sphere and update hover state.
   * Returns true if a sphere is hovered.
   */
  private updateAutoPromptHoverState(screenPos: THREE.Vector2): boolean {
    if (!this.autoPromptSpheresGroup || this.autoPrompts.length === 0) return false;

    // Find the closest sphere within hit radius
    let bestIndex = -1;
    let bestDist = Infinity;
    const HIT_SCREEN_PX = 14; // pixels

    for (const child of this.autoPromptSpheresGroup.children) {
      const sphere = child as THREE.Mesh;
      const idx = sphere.userData.autoPromptIndex as number;

      // Project sphere world position to screen
      const worldPos = sphere.position.clone();
      const camera = this.viewer.scene.getActiveCamera();
      const projected = worldPos.clone().project(camera);
      const screenW = this.viewer.renderArea.clientWidth;
      const screenH = this.viewer.renderArea.clientHeight;
      const sx = ((projected.x + 1) / 2) * screenW;
      const sy = ((1 - projected.y) / 2) * screenH;

      const dist = Math.hypot(screenPos.x - sx, screenPos.y - sy);
      if (dist < HIT_SCREEN_PX && dist < bestDist) {
        bestDist = dist;
        bestIndex = idx;
      }
    }

    if (bestIndex !== this.hoveredAutoPromptIndex) {
      this.hoveredAutoPromptIndex = bestIndex;
      if (bestIndex >= 0) {
        // Refresh sphere sizes to show hover highlight
        this.renderRegionGraph();
      }
    }

    return bestIndex >= 0;
  }

  /**
   * Handle mouse move for hover detection and plane preview
   */
  onMouseMove(event: MouseEvent): void {
    if (this.edgeOpController.isActive()) {
      const mouse = this.getMousePosition(event);
      if (mouse) {
        this.edgeOpController.onMouseMove(event, mouse);
        return;
      }
    }

    // Mouse position and plane preview are updated by LineTool before delegation

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    // Get camera mode
    const camera = this.viewer.scene.getActiveCamera();
    const is2DMode = camera.type === 'OrthographicCamera';

    // Handle right-drag deletion box
    if (this.rightDragStart && is2DMode) {
      const dragDistance = mouse.distanceTo(this.rightDragStart);
      if (dragDistance > 5) {
        if (!this.areaSelector.isSelecting()) {
          this.areaSelector.startSelection(this.rightDragStart);
        }
        this.areaSelector.updateSelection(mouse);
        this.viewer.renderer.domElement.style.cursor = 'crosshair';
      }
      return;
    }

    // Handle prompt sphere drag (move prompt to follow mouse, snap via pixel alignment)
    if (this.promptSphereDrag && is2DMode) {
      const now = performance.now();
      if (now - this.promptSphereDrag.lastUpdateTime >= 16) {
        this.promptSphereDrag.lastUpdateTime = now;
        this.movePromptToMouse(mouse);
      }
      this.viewer.renderer.domElement.style.cursor = 'grabbing';
      return;
    }

    // Handle perpendicular snap mode (shift key held with existing prompts)
    if (this.shiftDown && is2DMode && this.linePrompts.length > 0) {
      this.updatePerpendicularSnapPreview(mouse);
      this.viewer.renderer.domElement.style.cursor = 'crosshair';
      return; // Don't process other hover effects during perpendicular snap mode
    }

    // Handle box preview during drag from prompt line (2D mode)
    if (is2DMode && this.promptLineDrag && this.dragBoxStart) {
      const promptLineDrag = this.promptLineDrag;
      const dragDistance = mouse.distanceTo(this.dragBoxStart);
      if (dragDistance > 5) {
        // Get current world position. `screenToWorld` already projects
        // onto the active base plane; no Z-lock needed (pre-base-plane
        // this forced `z = startPoint.z` as a floor-plane defensive
        // snap, which on a wall plane forced a horizontal slice and
        // broke vertical box extrusion).
        const currentWorldPoint = this.coordinateTransformer.screenToWorld(mouse);
        if (currentWorldPoint) {
          // When drag started from an intersection, switch between the two meeting
          // edges using the angle bisector as the decision boundary
          if (this.promptLineDrag.snappedToEndpoint && this.promptLineDrag.connectedPrompt) {
            const dragVec = new THREE.Vector3().subVectors(currentWorldPoint, this.promptLineDrag.startPoint);
            // Get directions pointing AWAY from the intersection along each prompt
            const currentPrompt = this.promptLineDrag.prompt;
            const connectedPrompt = this.promptLineDrag.connectedPrompt;
            const currentAway = currentPrompt.direction.clone().normalize();
            const connectedAway = connectedPrompt.direction.clone().normalize();
            // Flip if the intersection is at the positive end (direction points toward it)
            // We want the direction pointing away from the intersection into the prompt body
            const currentPosPoint = currentPrompt.connections?.positive?.point;
            if (currentPosPoint && currentPosPoint.distanceTo(this.promptLineDrag.startPoint) < 0.01) {
              currentAway.negate();
            }
            const connectedPosPoint = connectedPrompt.connections?.positive?.point;
            if (connectedPosPoint && connectedPosPoint.distanceTo(this.promptLineDrag.startPoint) < 0.01) {
              connectedAway.negate();
            }
            // The bisector has equal angles to both away-directions
            const bisector = currentAway.clone().add(connectedAway).normalize();
            // Normal to the bisector, in the active base plane — separates
            // the two sides. Pre-fix this was a hardcoded XY 90° rotation
            // (`(-bisector.y, bisector.x, 0)`), which collapses any
            // vertical component to zero on wall-plane edits — every
            // diagonal bisector resolved to the same horizontal normal
            // and the side test broke.
            const bisectorNormal = this.boxExtrusionHandler.inPlanePerp(bisector);
            // Determine which side of the bisector the mouse is on
            const dragSide = dragVec.dot(bisectorNormal);
            const currentSide = currentAway.dot(bisectorNormal);
            // If the mouse is on the opposite side from the current prompt's away dir, switch
            if (Math.sign(dragSide) !== Math.sign(currentSide) && Math.abs(dragSide) > 0.001) {
              this.promptLineDrag.prompt = connectedPrompt;
              this.promptLineDrag.connectedPrompt = currentPrompt;
            }
          }

          // Calculate clamped box corners (clamped to prompt line endpoints)
          const box = this.boxExtrusionHandler.computeBoxCorners(this.promptLineDrag.startPoint, currentWorldPoint, this.promptLineDrag.prompt);

          if (box) {
            const { corner1, corner2, corner3, corner4, promptDir, perpDir, sideLength, perpLength } = box;
            this.visualizer.renderBoxPreview([corner1, corner2, corner3, corner4]);

            // Compute density-snapped green preview (skip when Ctrl held)
            const densityMap = this.lineDetectionService.cachedDensityMap;
            const positionMap = this.lineDetectionService.cachedPositionMap;
            const floorHeight = this.currentBasePlane?.origin.z ?? null;

            if (!this.ctrlDown && densityMap) {
              this.snappedBoxEdges = this.boxExtrusionHandler.computeSnappedBoxEdges(corner1, corner2, corner3, corner4, promptDir, perpDir, sideLength, perpLength, densityMap, positionMap, floorHeight, promptLineDrag.prompt);
              if (this.snappedBoxEdges) {
                this.visualizer.renderSnappedBoxPreview(this.snappedBoxEdges);
              }
            } else {
              this.snappedBoxEdges = null;
              this.visualizer.clearSnappedBoxPreview();
            }
          }

          this.viewer.renderer.domElement.style.cursor = 'crosshair';
          return;
        }
      }
    }

    // Update line preview if we have a first click point (two-click mode in 2D)
    if (this.firstClickPoint && is2DMode) {
      this.updateLinePreview(mouse);
      // Show crosshair cursor during two-click mode
      this.viewer.renderer.domElement.style.cursor = 'crosshair';
      // Keep preview lines hidden during two-click mode
      for (const line of this.detectedLines) {
        line.isHidden = true;
      }
      this.lineRenderer.render(this.detectedLines);
      return; // Don't process other hover effects during two-click mode
    }

    // Handle region dragging - continuously sample and add new regions
    if (is2DMode && this.regionBrushManager.regionDragging) {
      // Shift locks the stroke to a screen-orthogonal direction.
      const brushMouse = this.applyBrushAxisLock(mouse, event.shiftKey);
      this.renderBrushCursor(brushMouse);
      // The gesture became a real drag: fold the hover highlight into the
      // selection first, so painting extends what was highlighted instead of
      // forgetting the smartly collected islands. Threshold-gated so a
      // jittery click still goes through the click path's add-on-click.
      if (this.pendingBrushSeedRegionIds && this.dragBoxStart && mouse.distanceTo(this.dragBoxStart) > 5) {
        const seed = this.pendingBrushSeedRegionIds;
        this.pendingBrushSeedRegionIds = null;
        this.addRegionsToSelection(seed);
      }
      // Positions are batched and sent to the worker in self-draining flushes
      this.sampleAndAddRegions(brushMouse);
      return;
    }

    // Update line preview during drag-to-create-line (2D mode)
    // Drag creates temp prompts when a shape exists
    if (is2DMode && this.dragStartWorldPoint && this.dragBoxStart) {
      const dragDistance = mouse.distanceTo(this.dragBoxStart);
      if (dragDistance > 5) {
        // Show preview line from drag start to current position.
        // `screenToWorld` already projects onto `viewerMode.associatedPlane`
        // (floor for rooms, wall plane for door/window phase-2); no Z-lock
        // needed. Pre-base-plane this forced `z = dragStartWorldPoint.z`
        // which locked the preview horizontal on a wall plane.
        const currentWorldPoint = this.coordinateTransformer.screenToWorld(mouse);
        if (currentWorldPoint) {
          this.visualizer.renderLinePreview(this.dragStartWorldPoint, currentWorldPoint);
        }
        this.viewer.renderer.domElement.style.cursor = 'crosshair';
        // Keep preview lines hidden during drag
        for (const line of this.detectedLines) {
          line.isHidden = true;
        }
        this.lineRenderer.render(this.detectedLines);
        return; // Don't process other hover effects during drag
      }
    }

    // Don't process hover while camera is moving or detection is pending
    if (this.cameraMoving || this.cameraMoveTimeout !== null) return;

    // During Ctrl-refine, skip hover override management — the ctrl_refine
    // hint must stay active until refinement is finalized or cancelled.
    if (this.parentTool.isRefining) return;

    // Update prompt sphere hover state (works in both 2D and 3D modes)
    this.updatePromptHoverState(mouse);

    // Check if hovering over a prompt sphere - show grab cursor (draggable)
    const hoveredPrompt = this.hitTester.findHoveredPromptSphere(mouse, this.linePrompts);
    if (hoveredPrompt) {
      this.viewer.renderer.domElement.style.cursor = is2DMode ? 'grab' : 'pointer';
      // Clear preview line hover state when hovering over placed prompt
      this.updatePreviewLineHoverState(null);
      this.polygonRenderer.clearPreviewMesh();
      this.updateAutoPromptHoverState(mouse);
      // In 3D, hide the plane circle and hover preview so only the existing prompt is visible
      if (!is2DMode) {
        this.parentTool.activePlane.visible = false;
        this.parentTool.activePlane.unHighlight();
        this.visualizer.clearHoverPreview();
        if (this.hoverOverrideId !== 'right_click_delete_single') {
          this.hoverOverrideId = 'right_click_delete_single';
          this.parentTool.pushHintOverride('right_click_delete_single');
        }
      } else {
        if (this.hoverOverrideId !== 'drag_move') {
          this.hoverOverrideId = 'drag_move';
          this.parentTool.pushHintOverride('drag_move');
        }
      }

      return; // Don't process line hover if hovering over a prompt sphere
    }

    // Check if hovering over a prompt direction line - show crosshair cursor
    if (is2DMode && this.linePrompts.length > 0) {
      const promptLineHit = this.hitTester.findPromptLineAtPosition(mouse, this.linePrompts);
      if (promptLineHit) {
        this.viewer.renderer.domElement.style.cursor = 'crosshair';
        this.updatePreviewLineHoverState(null);
        this.polygonRenderer.clearPreviewMesh();
        if (this.hoverOverrideId !== 'wall_snap_drag') {
          this.hoverOverrideId = 'wall_snap_drag';
          this.parentTool.pushHintOverride('wall_snap_drag');
        }
        return;
      }
    }

    // Clear hover override if we were hovering but no longer are
    if (this.hoverOverrideId !== null) {
      this.hoverOverrideId = null;
      this.parentTool.popHintOverride();
    }

    // In 3D mode, check for editable-shape hover (pointer cursor, hide plane preview/visualizer)
    if (!is2DMode && !this.editingShape && this.linePrompts.length === 0) {
      if (this.parentTool.kindAdapter.isMouseOverEditableShape({ viewer: this.viewer, persona: this.persona, mouse: this.parentTool.mouse, coordinateTransformer: this.coordinateTransformer, basePlane: this.currentBasePlane })) {
        this.viewer.renderer.domElement.style.cursor = 'pointer';
        this.parentTool.activePlane.visible = false;
        this.parentTool.activePlane.unHighlight();
        this.visualizer.clearHoverPreview();
        return;
      }
    }

    // Check if hovering over the cutoff line (red line) when there are temporary prompts
    const hasTemporaryPrompts = this.linePrompts.some((p) => p.isTemporary);
    if (hasTemporaryPrompts && is2DMode && this.isNearCutoffLine(mouse)) {
      this.viewer.renderer.domElement.style.cursor = 'pointer';
      this.updatePreviewLineHoverState(null);
      this.polygonRenderer.clearPreviewMesh();
      return;
    }

    // Check if hovering over an auto-prompt sphere
    if (is2DMode && this.autoPrompts.length > 0) {
      const overAutoPrompt = this.updateAutoPromptHoverState(mouse);
      if (overAutoPrompt) {
        this.viewer.renderer.domElement.style.cursor = 'pointer';
        this.updatePreviewLineHoverState(null);
        this.polygonRenderer.clearPreviewMesh();
        return;
      }
    }

    // Line hover detection only in 2D mode
    if (is2DMode) {
      // When shift is held AND we have prompts, hide all preview lines (manual drawing mode)
      // When shift is held with no prompts, keep preview active for multi-selection
      if (this.shiftDown && this.linePrompts.length > 0) {
        // Hide all preview lines
        for (const line of this.detectedLines) {
          line.isHovered = false;
          line.isHidden = true;
        }
        this.lineRenderer.render(this.detectedLines);
        this.updatePreviewLineHoverState(null);
        this.polygonRenderer.clearPreviewMesh();
        this.viewer.renderer.domElement.style.cursor = 'crosshair';
        return;
      }

      // Check for room hover/inside (only when not editing and no prompts placed)
      if (this.isMouseOverRoom()) {
        this.viewer.renderer.domElement.style.cursor = 'pointer';
        // Clear all preview visuals when over room
        this.updatePreviewLineHoverState(null);
        this.polygonRenderer.clearPreviewMesh();
        this.polygonRenderer.clearPolygonRendering();
        this.clearRegionPolygonPreview();
        this.clearBrushCursor();
        return;
      }

      // Lines are only visible/hoverable when Ctrl is held (no prompts) or when prompts exist
      const linesActive = this.ctrlDown || this.linePrompts.length > 0;

      // When regions are selected, hide detected lines and show region preview
      if (this.regionBrushManager.selectedRegionIds.size > 0 && !linesActive) {
        for (const line of this.detectedLines) {
          line.isHovered = false;
          line.isHidden = true;
        }
        this.hoveredPreviewLineId = null;
        this.updatePreviewLineHoverState(null);
        this.lineRenderer.render(this.detectedLines);
        this.viewer.renderer.domElement.style.cursor = 'default';

        if (this.lineDetectionService.cachedPositionMap) {
          this.updatePreview(mouse);
        }
      } else {
        const hoveredLine = linesActive ? this.lineRenderer.getLineAtPosition(mouse.x, mouse.y, this.detectedLines) : null;

        this.hoveredPreviewLineId = hoveredLine?.id || null;

        for (const line of this.detectedLines) {
          line.isHovered = line === hoveredLine;
          line.isHidden = !linesActive;
        }

        this.updatePreviewLineHoverState(hoveredLine);
        this.lineRenderer.render(this.detectedLines);
        this.viewer.renderer.domElement.style.cursor = hoveredLine ? 'pointer' : 'default';

        // When Ctrl is held (line mode): hide brush/region preview entirely
        // When Ctrl is not held and no prompts: show brush/region preview
        // When prompts exist: always clear brush cursor
        if (this.linePrompts.length > 0) {
          this.clearBrushCursor();
        } else if (linesActive) {
          this.polygonRenderer.clearPreviewMesh();
          this.polygonRenderer.clearPolygonRendering();
          this.clearRegionPolygonPreview();
          this.clearBrushCursor();
        } else if (this.lineDetectionService.cachedPositionMap) {
          this.updatePreview(mouse);
        }
      }
    } else {
      // In 3D mode, reset cursor if not hovering over prompt
      this.updatePreviewLineHoverState(null);
      this.polygonRenderer.clearPreviewMesh();
      this.viewer.renderer.domElement.style.cursor = 'default';
    }
  }

  /**
   * Handle mouse down to start drag box or place prompts
   */
  onMouseDown(event: MouseEvent): void {
    const camera = this.viewer.scene.getActiveCamera();
    const is2DMode = camera.type === 'OrthographicCamera';

    if (!is2DMode) return; // only 2D logic is handled here right now, return early to prevent incorrect behavior

    // While the edge-op controller is in a pick-target / input phase, it
    // owns left-clicks. Defer to it before normal handling.
    if (this.edgeOpController.isActive()) {
      const mouse = this.getMousePosition(event);
      if (mouse && this.edgeOpController.onMouseDown(event, mouse)) return;
    }

    // Right mouse button: start right-drag for prompt deletion box
    if (event.button === 2) {
      const mouse = this.getMousePosition(event);
      if (mouse && this.linePrompts.length > 0) {
        this.rightDragStart = mouse.clone();
        this.parentTool.pushHintOverride('right_click_delete');
      }
      return;
    }

    // Only handle left mouse button
    if (event.button !== 0) return;

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    // Track mouse down position for drag detection
    this.dragBoxStart = mouse.clone();

    // PRIORITY: Check if clicking on a prompt sphere (drag to move) - this takes priority over all other inputs
    if (is2DMode && this.linePrompts.length > 0 && !this.firstClickPoint) {
      const hoveredPrompt = this.hitTester.findHoveredPromptSphere(mouse, this.linePrompts);
      if (hoveredPrompt) {
        // Snapshot neighbor positions for cancel support
        const neighborSnapshots: Array<{
          prompt: LinePrompt;
          worldPoint: THREE.Vector3;
          screenPosition: THREE.Vector2;
          farPrompt: LinePrompt | null;
          side?: 'pos' | 'neg';
        }> = [];
        const hovConns = hoveredPrompt.connections;
        if (hovConns) {
          const posConn: PromptConnection | undefined = hovConns.positive;
          const negConn: PromptConnection | undefined = hovConns.negative;
          const connSides: Array<{ conn: PromptConnection | undefined; side: 'pos' | 'neg' }> = [
            { conn: posConn, side: 'pos' },
            { conn: negConn, side: 'neg' },
          ];
          for (const { conn, side } of connSides) {
            if (!conn) continue;
            const neighborIdx = conn.targetPromptIndex;
            if (neighborIdx >= 0 && neighborIdx < this.linePrompts.length) {
              const neighborPrompt = this.linePrompts[neighborIdx];
              if (neighborPrompt !== hoveredPrompt) {
                // Only slide neighbors of the same kind:
                // - Temp prompts only slide other temp prompts
                // - Regular prompts only slide other regular prompts
                if (!!hoveredPrompt.isTemporary !== !!neighborPrompt.isTemporary) continue;

                // Find the far-side prompt (neighbor's other connection, away from dragged prompt)
                let farPrompt: LinePrompt | null = null;
                const nPos: PromptConnection | undefined = neighborPrompt.connections?.positive;
                const nNeg: PromptConnection | undefined = neighborPrompt.connections?.negative;
                for (const nConn of [nPos, nNeg]) {
                  if (!nConn) continue;
                  const farIdx: number = nConn.targetPromptIndex;
                  if (farIdx >= 0 && farIdx < this.linePrompts.length) {
                    const farCandidate: LinePrompt = this.linePrompts[farIdx];
                    if (farCandidate !== hoveredPrompt && farCandidate !== neighborPrompt) {
                      farPrompt = farCandidate;
                      break;
                    }
                  }
                }
                neighborSnapshots.push({
                  prompt: neighborPrompt,
                  worldPoint: neighborPrompt.worldPoint.clone(),
                  screenPosition: neighborPrompt.screenPosition.clone(),
                  farPrompt,
                  side,
                });
              }
            }
          }
        }

        // Snapshot before any drag mutations so Ctrl+Z restores the pre-drag state
        this.parentTool.pushUndoSnapshot();

        this.promptSphereDrag = {
          prompt: hoveredPrompt,
          originalWorldPoint: hoveredPrompt.worldPoint.clone(),
          originalDirection: hoveredPrompt.direction.clone(),
          originalPosLength: hoveredPrompt.positiveLength,
          originalNegLength: hoveredPrompt.negativeLength,
          lastUpdateTime: 0,
          lastFittedDirection: null,
          neighborSnapshots,
        };
        // Disable orbital controls during prompt drag
        this.viewer.controls.enabled = false;
        this.viewer.renderer.domElement.style.cursor = 'grabbing';
        this.parentTool.pushHintOverride('drag_move');
        return;
      }
    }

    // In 2D mode, check if starting drag from a prompt's direction line
    if (is2DMode && this.linePrompts.length > 0 && !this.firstClickPoint) {
      // Check if mouse is on a prompt's direction line (not the center sphere)
      const promptLineHit = this.hitTester.findPromptLineAtPosition(mouse, this.linePrompts);
      if (promptLineHit) {
        // Start prompt line drag for box creation
        this.promptLineDrag = {
          prompt: promptLineHit.prompt,
          startPoint: promptLineHit.worldPoint.clone(),
          side: promptLineHit.side,
          snappedToEndpoint: promptLineHit.snappedToEndpoint,
        };
        // When snapped to an intersection, find the connected prompt for edge switching.
        // Use geometric search rather than connections (which can be stale for temp prompts).
        if (promptLineHit.snappedToEndpoint) {
          const neighbor = this.boxExtrusionHandler.findNeighborAtEndpoint(promptLineHit.worldPoint, promptLineHit.prompt, this.linePrompts);
          if (neighbor) {
            this.promptLineDrag.connectedPrompt = neighbor;
          }
        }
        // Disable orbital controls during box drag
        this.viewer.controls.enabled = false;
        this.parentTool.pushHintOverride('wall_snap_drag');
        return;
      }
    }

    // In 2D mode without shift, capture the world position for drag-to-create-line feature
    // (This creates temp prompts when a shape already exists)
    // Always capture the start point - if we end up dragging off a detected line, we still want to create a free line
    //
    // Rectangle-only lock applies at drag START, not just at mouse-up: without
    // it the drag still rendered the line preview for its whole duration and
    // created a segment that mouse-up immediately discarded — for door/window
    // (rectangleOnly) kinds no extra side can ever be added, so the drag must
    // not begin at all.
    if (is2DMode && !this.shiftDown && !this.firstClickPoint && this.linePrompts.length > 0 && !this.rectangleLocked()) {
      // Get world position for potential drag start (line creation)
      const worldPoint = this.coordinateTransformer.screenToWorld(mouse);
      if (worldPoint) {
        this.dragStartWorldPoint = worldPoint;
        // Track if we started on a detected line - if we just click (no drag), we'll select the line
        // But if we drag away, we'll create a free line from the start point
        this.dragStartedOnDetectedLine = !!this.lineRenderer.getLineAtPosition(mouse.x, mouse.y, this.detectedLines);
      }
    }

    // When hovering a region or have selected regions, start region dragging.
    // Shift does NOT block the brush here (it has no other meaning without
    // prompts): a pre-held Shift starts the stroke axis-locked right away.
    if (is2DMode && this.linePrompts.length === 0 && !this.ctrlDown) {
      if (this.regionBrushManager.selectedRegionIds.size > 0 || this.lastPreviewRectResult?.regionIds.length) {
        this.regionBrushManager.regionDragging = true;
        this.lastBrushAxis = null;
        this.brushAxisLock = event.shiftKey ? { anchor: mouse.clone(), axis: null } : null;
        // Snapshot the hover highlight (the smartly collected neighbor
        // islands) — folded into the selection once this becomes a real drag
        // (see the regionDragging branch in onMouseMove). A plain click keeps
        // its own add-on-click path, so nothing is added here yet.
        this.pendingBrushSeedRegionIds = this.lastPreviewRectResult?.regionIds.length ? [...this.lastPreviewRectResult.regionIds] : null;
      }
    }
  }

  /**
   * Constrain the brush position to a screen-orthogonal direction while
   * Shift is held. The first engagement of a stroke anchors where Shift
   * engages (before the drag or mid-stroke alike) and latches onto the
   * dominant screen axis once the cursor moves a few pixels. Releasing and
   * re-holding Shift rotates the lock 90° from the previous engagement's
   * axis, anchored at the current position — so alternating holds paint
   * clean right-angle runs.
   */
  private applyBrushAxisLock(mouse: THREE.Vector2, shiftHeld: boolean): THREE.Vector2 {
    if (!shiftHeld) {
      this.brushAxisLock = null;
      return mouse;
    }
    if (!this.brushAxisLock) {
      // Re-engagement within the same stroke: rotate 90° from the last axis.
      const axis = this.lastBrushAxis === null ? null : this.lastBrushAxis === 'x' ? 'y' : 'x';
      this.brushAxisLock = { anchor: mouse.clone(), axis };
      if (axis) this.lastBrushAxis = axis;
      return mouse;
    }
    const lock = this.brushAxisLock;
    if (lock.axis === null) {
      const dx = Math.abs(mouse.x - lock.anchor.x);
      const dy = Math.abs(mouse.y - lock.anchor.y);
      if (Math.max(dx, dy) < 4) return mouse; // not committed to a direction yet
      lock.axis = dx >= dy ? 'x' : 'y';
      this.lastBrushAxis = lock.axis;
    }
    return lock.axis === 'x' ? new THREE.Vector2(mouse.x, lock.anchor.y) : new THREE.Vector2(lock.anchor.x, mouse.y);
  }

  /**
   * Handle mouse up to complete drag box or place prompts
   */
  onMouseUp(event: MouseEvent): void {
    // Right mouse button: complete deletion drag box
    if (event.button === 2) {
      if (this.rightDragStart && this.areaSelector.isSelecting()) {
        const mouse = this.getMousePosition(event);
        if (mouse) {
          this.deletePromptsInBox(this.rightDragStart, mouse);
        }
        this.areaSelector.cancelSelection();
        this.rightDragWasBox = true;
      }
      // Clear deletion highlighting
      this.deletionManager.clearHighlighting(this.linePrompts, this.hoveredPromptId);
      this.rightDragStart = null;
      this.viewer.renderer.domElement.style.cursor = 'default';
      this.parentTool.popHintOverride();
      return;
    }

    // Only handle left mouse button
    if (event.button !== 0) return;

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    // Handle prompt sphere drag completion. If the cursor barely moved
    // (< 5px), treat as a CLICK — restore the pre-drag snapshot and (when
    // the kind opts in) open the radial edge-op menu.
    if (this.promptSphereDrag) {
      const CLICK_THRESHOLD_PX = 5;
      const movedFromStart = this.dragBoxStart ? mouse.distanceTo(this.dragBoxStart) : Infinity;
      if (movedFromStart <= CLICK_THRESHOLD_PX) {
        const drag = this.promptSphereDrag;
        drag.prompt.worldPoint.copy(drag.originalWorldPoint);
        drag.prompt.direction.copy(drag.originalDirection);
        drag.prompt.positiveLength = drag.originalPosLength;
        drag.prompt.negativeLength = drag.originalNegLength;
        drag.prompt.screenPosition = this.coordinateTransformer.worldToScreen(drag.originalWorldPoint);
        for (const snap of drag.neighborSnapshots) {
          snap.prompt.worldPoint.copy(snap.worldPoint);
          snap.prompt.screenPosition.copy(snap.screenPosition);
        }
        this.parentTool.undoRedo?.popState();
        this.updateShapePreview();

        this.promptSphereDrag = null;
        this.lastDragMouse = null;
        this.dragBoxStart = null;
        this.visualizer.clearSnapTargets();
        this.constraintSnapVisuals.clear();
        this.viewer.controls.enabled = true;
        this.viewer.renderer.domElement.style.cursor = 'default';
        this.parentTool.popHintOverride();

        if (this.parentTool.kindAdapter.enableEdgeOpMenu) {
          this.edgeOpController.openMenuFor(drag.prompt);
        }
        return;
      }

      this.movePromptToMouse(mouse);

      // Snapshot prompts before cleanup so we can restore deleted prompts if the shape breaks
      const promptsBeforeCleanup = [...this.linePrompts];
      this.cleanupEdgesAfterDrag(this.promptSphereDrag.prompt);

      // Check if the shape is still valid after the drag
      // Only for non-temporary prompts (real prompts that form the main shape)
      const draggedPrompt = this.promptSphereDrag.prompt;
      if (!draggedPrompt.isTemporary) {
        // Rebuild shape to check validity
        const regularPrompts = this.linePrompts.filter((p) => !p.isTemporary);
        const shape = this.shapeBuilder.createShapeFromPrompts(regularPrompts);

        // Shape is valid only if it has at least 3 vertices AND uses all prompts
        // Each prompt contributes one edge, so vertices should equal number of prompts
        const isValidShape = shape && shape.vertices.length >= 3 && shape.vertices.length === regularPrompts.length;

        if (!isValidShape) {
          // Restore the full prompts array (including any prompts deleted by cleanup)
          this.linePrompts = promptsBeforeCleanup;

          // Reset prompt to original position
          const { originalWorldPoint, originalDirection, originalPosLength, originalNegLength, neighborSnapshots } = this.promptSphereDrag;
          draggedPrompt.worldPoint.copy(originalWorldPoint);
          draggedPrompt.direction.copy(originalDirection);
          draggedPrompt.positiveLength = originalPosLength;
          draggedPrompt.negativeLength = originalNegLength;
          draggedPrompt.screenPosition = this.coordinateTransformer.worldToScreen(originalWorldPoint);

          // Restore neighbor positions
          for (const snapshot of neighborSnapshots) {
            snapshot.prompt.worldPoint.copy(snapshot.worldPoint);
            snapshot.prompt.screenPosition.copy(snapshot.screenPosition);
          }

          // Rebuild shape preview with restored positions
          this.updateShapePreview();
        }
      }

      this.promptSphereDrag = null;
      this.lastDragMouse = null;
      this.dragBoxStart = null;
      this.visualizer.clearSnapTargets();
      this.constraintSnapVisuals.clear();
      // Re-enable orbital controls after drag ends (in case ctrl was held)
      this.viewer.controls.enabled = true;
      this.viewer.renderer.domElement.style.cursor = 'default';
      this.parentTool.popHintOverride();
      return;
    }

    // Stop region dragging
    const wasRegionDragging = this.regionBrushManager.regionDragging;
    this.regionBrushManager.regionDragging = false;
    this.pendingBrushSeedRegionIds = null;
    this.brushAxisLock = null;
    this.lastBrushAxis = null;

    const camera = this.viewer.scene.getActiveCamera();
    const is3DMode = camera.type === 'PerspectiveCamera';

    // Check if this was a drag (mouse moved significantly from start)
    const dragThreshold = 5; // pixels
    const wasDrag = this.dragBoxStart && mouse.distanceTo(this.dragBoxStart) > dragThreshold;

    // If we were region dragging, finalize: expand regions + polygon processing
    if (wasRegionDragging && wasDrag) {
      this.dragBoxStart = null;
      this.dragStartWorldPoint = null;

      // Finalize brush: flush remaining positions, expand regions, trigger polygon
      this.finalizeBrushDrag().then(() => {
        // Sync the worker's selection with the expanded set
        if (this.regionBrushManager.selectedRegionIds.size > 0) {
          this.lineDetectionService.updateSelectedRegions(Array.from(this.regionBrushManager.selectedRegionIds));
        }

        if (this.regionBrushManager.selectedRegionIds.size > 0 && !this.debugWidget?.requireEnterConfirm) {
          if (this.cachedProcessedPolygon) {
            this.confirmedPolygonSnapshot = this.cachedProcessedPolygon;
            this.confirmRegionSelection(true);
          } else {
            this.regionBrushManager.autoConfirmPending = true;
          }
        }
      });
      return;
    }

    // In 3D mode, click handling is done by LineTool.onClick (BaseTool filters drags)
    if (is3DMode) return;

    // 2D mode below

    // Handle box creation from prompt line drag
    if (wasDrag && this.promptLineDrag) {
      const endWorldPoint = this.coordinateTransformer.screenToWorld(mouse);
      if (endWorldPoint) {
        this.parentTool.pushUndoSnapshot();
        // `screenToWorld` already on the active base plane; no Z-lock.

        // When ctrl is held, snappedBoxEdges is null. Build raw edges from the
        // purple preview corners so the final result matches exactly (no density snap).
        let edges = this.snappedBoxEdges;
        if (!edges) {
          const box = this.boxExtrusionHandler.computeBoxCorners(this.promptLineDrag.startPoint, endWorldPoint, this.promptLineDrag.prompt);
          if (box) {
            edges = [
              [box.corner2.clone(), box.corner4.clone()],
              [box.corner4.clone(), box.corner3.clone()],
              [box.corner3.clone(), box.corner1.clone()],
            ];
          }
        }

        this.createBoxFromPromptLineDrag(this.promptLineDrag, endWorldPoint, edges);
      }
      // Clear drag state
      this.promptLineDrag = null;
      this.snappedBoxEdges = null;
      this.dragBoxStart = null;
      this.visualizer.clearBoxPreview();
      this.viewer.controls.enabled = true;
      this.parentTool.popHintOverride();
      return;
    }

    // Clear prompt line drag state if it was just a click (not a drag)
    if (this.promptLineDrag) {
      this.promptLineDrag = null;
      this.snappedBoxEdges = null;
      this.viewer.controls.enabled = true;
      this.parentTool.popHintOverride();
    }

    // Handle drag-to-create-line: if we have a drag start point and actually dragged, complete the line
    // This also handles the case where drag started on a detected line but user dragged away
    if (wasDrag && this.dragStartWorldPoint && !this.firstClickPoint) {
      // Rectangle-only lock: no additional sides once the rectangle exists.
      if (this.rectangleLocked()) {
        this.dragBoxStart = null;
        this.dragStartWorldPoint = null;
        this.dragStartedOnDetectedLine = false;
        this.visualizer.clearLinePreview();
        return;
      }
      // Get world position for drag end. `screenToWorld` already on
      // the active base plane; no Z-lock needed (was a floor-plane
      // defensive snap that locked vertical drag on a wall).
      const endWorldPoint = this.coordinateTransformer.screenToWorld(mouse);
      if (endWorldPoint) {
        // Create line prompt from the drag start to drag end
        this.createLinePromptFromTwoPoints(this.dragStartWorldPoint, endWorldPoint);

        // Clear drag state
        this.dragBoxStart = null;
        this.dragStartWorldPoint = null;
        this.dragStartedOnDetectedLine = false;
        this.visualizer.clearLinePreview();
        this.showPreviewLines();
        return;
      }
    }

    // If we have a drag start but didn't actually drag (no area selector active),
    // this is a click - handle prompt placement
    if (this.dragBoxStart) {
      this.dragBoxStart = null;
      this.dragStartWorldPoint = null;
      this.dragStartedOnDetectedLine = false;
      // In 2D mode, handle line selection or manual line creation
      this.handle2DLineClick(event);
    }
  }

  /**
   * Handle click to select lines and place prompts
   * In 2D mode: Click on detected lines
   * In 3D mode: Click to create prompt from preview plane position
   */
  onClick(_event: MouseEvent): void {
    // Click handling is now done in onMouseUp for better drag detection
  }

  /**
   * Handle click in 2D mode - detected line selection, room editing, or manual line creation (with shift)
   */
  private handle2DLineClick(event: MouseEvent): void {
    // Don't allow clicks while camera is moving or detection is pending
    if (this.cameraMoving || this.cameraMoveTimeout !== null) {
      return;
    }

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    // Get world position for the click
    const worldPoint = this.coordinateTransformer.screenToWorld(mouse);
    if (!worldPoint) return;

    // Rectangle-only lock: once the rectangle's sides exist, clicks may only
    // enter edit mode (via the hit-test below) — no new prompts or lines.
    const rectLocked = this.rectangleLocked();
    if (rectLocked) this.firstClickPoint = null;

    // Handle perpendicular snap mode (shift key held)
    if (!rectLocked && this.shiftDown && this.perpendicularSnapTarget) {
      this.createPerpendicularSnapPrompt();
      return;
    }

    // Check for merge trigger: click on main shape while having temporary prompts
    const hasTemporaryPrompts = this.linePrompts.some((p) => p.isTemporary);
    if (!rectLocked && hasTemporaryPrompts && this.isClickOnMainShape(mouse)) {
      this.mergeTemporaryShape();
      return;
    }

    // Check if we have a first click point (two-click mode - only when shift is held)
    if (this.firstClickPoint) {
      // Second click - create line prompt from two points
      this.createLinePromptFromTwoPoints(this.firstClickPoint, worldPoint);
      this.firstClickPoint = null;
      this.visualizer.clearLinePreview();
      this.showPreviewLines();
      return;
    }

    // If not already editing a shape and no prompts placed, check for editable-shape click
    // via the kind adapter so this 2D path mirrors the 3D handle3DClick path.
    if (!this.editingShape && this.linePrompts.length === 0) {
      const adapterCtx = { viewer: this.viewer, persona: this.persona, mouse: this.parentTool.mouse, coordinateTransformer: this.coordinateTransformer, basePlane: this.currentBasePlane };
      const clickedShape = this.parentTool.kindAdapter.hitTestEditableTarget(adapterCtx);
      if (clickedShape) {
        this.parentTool.kindAdapter.configureViewForShape(clickedShape, this.viewer);
        this.enterShapeEditMode(clickedShape);
        return;
      }
    }

    // Click on a depth region preview — add hovered regions to confirmed selection, process polygon, then auto-confirm
    if (!this.ctrlDown && this.linePrompts.length === 0 && this.lastPreviewRectResult && this.lastPreviewRectResult.regionIds.length > 0) {
      const added = this.addRegionsToSelection(this.lastPreviewRectResult.regionIds);
      if (added) {
        if (!this.debugWidget?.requireEnterConfirm) {
          this.regionBrushManager.autoConfirmPending = true;
        }
        const allIds = Array.from(this.regionBrushManager.selectedRegionIds).sort((a, b) => a - b);
        const cacheKey = `regions_${allIds.join('_')}`;
        this.triggerPolygonProcessing(this.lastPreviewRectResult.imageData, cacheKey);
      }
      return;
    }

    // Rectangle-only lock: nothing below may run — every remaining branch
    // creates a prompt or starts a new line.
    if (rectLocked) return;

    // Check if clicking on a detected line — when Ctrl held (no prompts) or prompts exist
    // This must be checked BEFORE entering two-click mode so line clicks take priority
    const linesClickable = this.ctrlDown || this.linePrompts.length > 0;
    const clickedLine = linesClickable ? this.lineRenderer.getLineAtPosition(mouse.x, mouse.y, this.detectedLines) : null;

    if (clickedLine) {
      // Clicking on detected line - create prompt at click position
      this.handleLineClick(clickedLine, event);
      return;
    }

    // Click (without shift): manual line drawing mode for temp prompts
    // Allow when there are prompts OR when there's a current shape (for temporary prompts)
    // Only enters two-click mode if not clicking on a detected line
    const canEnterTwoClickMode = !this.shiftDown && (this.linePrompts.length > 0 || this.shapeRendererModule.currentShape !== null);
    if (canEnterTwoClickMode) {
      this.firstClickPoint = worldPoint.clone();
      this.updateLinePreview(mouse);
      return;
    }
    // If not clicking on a line and can't enter two-click mode, do nothing
  }

  /**
   * Add region IDs to the confirmed selection.
   * Also flood-fills: any unselected neighbor whose shared boundary with
   * already-selected nodes is >= 60% of its perimeter is auto-added.
   * Only updates the worker selection — does NOT trigger polygon processing.
   * Call triggerPolygonProcessing separately when you want a polygon update.
   */
  private addRegionsToSelection(regionIds: number | number[]): boolean {
    const ids = Array.isArray(regionIds) ? regionIds : [regionIds];
    const hasNew = this.regionBrushManager.addRegions(ids);

    if (hasNew) {
      // Mark newly added nodes in the region graph
      if (this.regionGraph) {
        for (const id of ids) {
          const node = this.regionGraph.nodes.get(id);
          if (node) node.selected = true;
        }
      }

      // Clear cache but keep cachedProcessedPolygon until new one is ready
      this.regionPolygonCache.clear();
      this.confirmedPolygonSnapshot = null;
      this.lineDetectionService.updateSelectedRegions(Array.from(this.regionBrushManager.selectedRegionIds)).then((result) => {
        // Skip if line prompts exist (selection was converted to prompts)
        if (this.linePrompts.length > 0) return;
        if (result.imageData) {
          this.polygonRenderer.renderPreviewMask(result.imageData, result.worldCorners);
        }
        // Worker expanded the selection (flood-fill + islands) — accept its authoritative set
        if (result.allSelectedIds && result.allSelectedIds.length > 0) {
          this.regionBrushManager.setRegionIds(new Set(result.allSelectedIds));
          if (this.regionGraph) {
            for (const id of result.allSelectedIds) {
              const node = this.regionGraph.nodes.get(id);
              if (node) node.selected = true;
            }
          }
          this.renderRegionGraph();
        }
      });

      // Re-render graph to show selected state
      this.renderRegionGraph();
    }

    return hasNew;
  }

  // ─── Brush cursor ring ──────────────────────────────────────────────────

  /** Delegate brush cursor rendering to RegionBrushManager */
  private renderBrushCursor(screenPos: THREE.Vector2): void {
    this.regionBrushManager.renderBrushCursor(screenPos);
  }

  /** Delegate brush cursor cleanup to RegionBrushManager */
  private clearBrushCursor(): void {
    this.regionBrushManager.clearBrushCursor();
  }

  /**
   * Handle shift + scroll wheel to adjust brush size.
   * Auto-bound by BaseTool. Only intercepts when shift is held — normal scroll/zoom is unaffected.
   */
  onWheel(event: WheelEvent): void {
    if (!event.shiftKey) return;
    if (this.linePrompts.length > 0) return;

    // Consume the event — prevent InputHandler from forwarding to controls
    event.preventDefault();
    event.stopPropagation();

    const delta = event.deltaY > 0 ? -5 : 5;
    this.regionBrushManager.adjustBrushSize(delta);

    // Re-render cursor at current mouse position
    const rect = this.viewer.renderArea.getBoundingClientRect();
    const mousePos = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
    this.renderBrushCursor(mousePos);
  }

  private pendingBrushPositions: Array<{ normalizedX: number; normalizedY: number; halfSizePixels: number }> = [];
  private brushBatchInFlight = false;

  /**
   * Queue a brush position for batched painting.
   * Converts mouse to mask coordinates and adds to the pending batch.
   * Triggers a flush if no batch is currently in flight.
   */
  private sampleAndAddRegions(mouse: THREE.Vector2): void {
    const worldPos = this.coordinateTransformer.screenToWorld(mouse);
    if (!worldPos) return;

    const maskPos = this.coordinateTransformer.worldToMaskSpace(worldPos);
    if (maskPos.x < 0 || maskPos.x > 1 || maskPos.y < 0 || maskPos.y > 1) return;

    const halfSizePixels = this.getBrushHalfSizeInMaskPixels();
    this.pendingBrushPositions.push({
      normalizedX: maskPos.x,
      normalizedY: maskPos.y,
      halfSizePixels,
    });

    this.flushBrushBatch();
  }

  /**
   * Self-draining batch flush: sends accumulated positions to the worker,
   * then checks for more on completion. Skips if a batch is already in flight.
   */
  private async flushBrushBatch(): Promise<void> {
    if (this.brushBatchInFlight || this.pendingBrushPositions.length === 0) return;

    this.brushBatchInFlight = true;
    try {
      while (this.pendingBrushPositions.length > 0) {
        // Take all accumulated positions
        const positions = this.pendingBrushPositions;
        this.pendingBrushPositions = [];

        const paintResult = await this.lineDetectionService.paintRectBatch(positions);

        if (paintResult) {
          // Display mask for visual feedback
          this.polygonRenderer.renderPreviewMask(paintResult.imageData, paintResult.worldCorners);
          this.lastPreviewRectResult = paintResult;

          // Accept the worker's authoritative set — direct hits plus the live
          // expansion (flood-fill/islands/pockets) it runs on every new hit.
          if (paintResult.allSelectedIds && paintResult.allSelectedIds.length > 0) {
            this.regionBrushManager.setRegionIds(new Set(paintResult.allSelectedIds));
            if (this.regionGraph) {
              for (const id of paintResult.allSelectedIds) {
                const node = this.regionGraph.nodes.get(id);
                if (node) node.selected = true;
              }
            }
            this.renderRegionGraph();
          }

          // Queue polygon processing (uses existing self-draining queue that skips intermediate states)
          if (paintResult.imageData) {
            this.regionPolygonCache.clear();
            this.confirmedPolygonSnapshot = null;
            const allIds = Array.from(this.regionBrushManager.selectedRegionIds).sort((a, b) => a - b);
            const cacheKey = `paint_${allIds.join('_')}_${Date.now()}`;
            this.triggerPolygonProcessing(paintResult.imageData, cacheKey);
          }
        }
        // Loop continues if more positions accumulated during the await
      }
    } finally {
      this.brushBatchInFlight = false;
    }
  }

  /**
   * Finalize the brush selection after drag ends: flush remaining positions,
   * run region expansion, and trigger polygon processing.
   */
  private async finalizeBrushDrag(): Promise<void> {
    // Flush any remaining positions first
    if (this.pendingBrushPositions.length > 0) {
      // Wait for in-flight batch to finish, then flush remaining
      while (this.brushBatchInFlight) {
        await new Promise((resolve) => setTimeout(resolve, 1));
      }
      await this.flushBrushBatch();
    } else if (this.brushBatchInFlight) {
      // Wait for the last batch to finish
      while (this.brushBatchInFlight) {
        await new Promise((resolve) => setTimeout(resolve, 1));
      }
    }

    // Run region expansion + build clean mask
    const finalResult = await this.lineDetectionService.finalizeBrushSelection();
    if (!finalResult) return;

    // Render the expanded display mask
    this.polygonRenderer.renderPreviewMask(finalResult.imageData, finalResult.worldCorners);
    this.lastPreviewRectResult = finalResult;

    // Update region selection with expanded set
    if (finalResult.allSelectedIds && finalResult.allSelectedIds.length > 0) {
      this.regionBrushManager.setRegionIds(new Set(finalResult.allSelectedIds));
      if (this.regionGraph) {
        for (const id of finalResult.allSelectedIds) {
          const node = this.regionGraph.nodes.get(id);
          if (node) node.selected = true;
        }
      }
      this.renderRegionGraph();
    }

    // Trigger polygon processing with the final mask
    if (finalResult.imageData) {
      this.regionPolygonCache.clear();
      this.confirmedPolygonSnapshot = null;
      const allIds = Array.from(this.regionBrushManager.selectedRegionIds).sort((a, b) => a - b);
      const cacheKey = `paint_${allIds.join('_')}_${Date.now()}`;
      this.triggerPolygonProcessing(finalResult.imageData, cacheKey);
    }
  }

  /**
   * Clear the multi-region selection.
   */
  private clearRegionSelection(): void {
    // Reset selected flags in region graph
    if (this.regionGraph) {
      for (const [, node] of this.regionGraph.nodes) {
        node.selected = false;
      }
    }
    this.regionBrushManager.clearSelection();
    // Clear any pending brush batch
    this.pendingBrushPositions = [];
    // Also clear the cached selection in the worker
    this.lineDetectionService.updateSelectedRegions([]);
    // Re-render graph to reflect cleared selection
    this.renderRegionGraph();
  }

  /**
   * Confirm the current region selection and clean up.
   * Converts the polygon to line prompts and clears all region state.
   * @param convertToPrompts If true, convert the polygon to line prompts. If false, just clear.
   */
  private confirmRegionSelection(convertToPrompts: boolean): void {
    if (convertToPrompts) {
      this.convertCachedPolygonToLinePrompts();
    }
    // Clear the mask and polygon visuals after confirming
    this.polygonRenderer.clearPreviewMesh();
    this.polygonRenderer.clearPolygonRendering();
    this.cachedProcessedPolygon = null;
    this.pendingPolygonMask = null;
    // Clear the region selection
    // Note: Late worker results are ignored because linePrompts.length > 0 after conversion
    this.clearRegionSelection();

    // Adapter-controlled auto-commit: when the kind doesn't want the
    // intermediate prompt-handle state for new shapes (door/window:
    // segmentation result already is the final rectangle), skip straight to
    // finalize. Edit flow is unaffected — `editingShape` is set when the
    // user click-edits an existing annotation, and in that case we keep
    // the prompts on-screen so they can be adjusted.
    if (convertToPrompts && this.linePrompts.length >= 3 && !this.editingShape && this.parentTool.kindAdapter.autoCommitNewFromRegion) {
      this.finalizeShape();
      return;
    }

    // Run line detection now that prompts exist
    if (convertToPrompts && this.linePrompts.length > 0) {
      this.detectLines();
    }
  }

  /**
   * Cancel the current region selection without converting to prompts.
   * Clears all region state and restarts hover preview.
   */
  private cancelRegionSelection(): void {
    // Clear the mask and polygon visuals
    this.polygonRenderer.clearPreviewMesh();
    this.polygonRenderer.clearPolygonRendering();
    this.clearRegionPolygonPreview();
    this.cachedProcessedPolygon = null;
    this.pendingPolygonMask = null;
    this.regionPolygonCache.clear();
    this.clearBrushCursor();
    // Clear the region selection (this updates the worker too)
    this.clearRegionSelection();
    // Reset preview position to force a new preview
    this.lastPreviewPosition = null;
  }

  /**
   * Convert the confirmed polygon snapshot to LinePrompts.
   * Uses the snapshot captured when user lifted mouse (confirmedPolygonSnapshot).
   * Falls back to cachedProcessedPolygon if no snapshot exists.
   * Called when Enter is pressed or clicking the preview.
   */
  private convertCachedPolygonToLinePrompts(): void {
    // Rectangle-only kinds: a region confirm must never APPEND prompts to the
    // rectangle already on screen (mid-edit or post-segmentation) — the extra
    // vertices would break it. Fresh shapes (no prompts, not editing) convert
    // normally.
    if (this.rectangleLocked()) return;
    // Use snapshot if available (captured when user lifted mouse), otherwise use cached
    const polygon = this.confirmedPolygonSnapshot ?? this.cachedProcessedPolygon;
    if (!polygon) return;

    // Clear snapshot after use
    this.confirmedPolygonSnapshot = null;

    this.parentTool.pushUndoSnapshot();

    // Convert polygon points from pixel space to world space. Pre-adapter
    // we hardcoded a `worldPos.z = floorZ` flatten here, which destroyed
    // the Z component for vertical (door/window) shapes. The kind adapter
    // now owns the projection — rooms still flatten to floor, door/window
    // projects onto the wall plane.
    const rawWorldPoints: THREE.Vector3[] = [];
    for (const pt of polygon.points) {
      const worldPos = this.coordinateTransformer.pixelToWorld(pt.x, pt.y);
      if (worldPos) rawWorldPoints.push(worldPos);
    }
    const worldPoints = this.parentTool.kindAdapter.snapPolygonToBasePlane(rawWorldPoints, { viewer: this.viewer, basePlane: this.currentBasePlane, shape: this.editingShape });

    if (worldPoints.length >= 3) {
      // Convert each polygon edge to a LinePrompt. Compute direction
      // and length from the full 3D edge so vertical (door/window)
      // polygon edges keep their Z component. Pre-base-plane this used
      // a hardcoded XY-only direction (`new Vector3(dx/len, dy/len, 0)`),
      // which dropped vertical edges to length 0 on a wall plane.
      for (let i = 0; i < worldPoints.length; i++) {
        const p1 = worldPoints[i];
        const p2 = worldPoints[(i + 1) % worldPoints.length];

        const edge = new THREE.Vector3().subVectors(p2, p1);
        const edgeLength = edge.length();
        const halfLength = edgeLength / 2;

        let direction: THREE.Vector3;
        if (edgeLength > 1e-10) {
          direction = edge.clone().multiplyScalar(1 / edgeLength);
        } else {
          direction = new THREE.Vector3(1, 0, 0);
        }

        const midpoint = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

        this.linePrompts.push({
          id: `prompt_${this.nextPromptId++}`,
          worldPoint: midpoint,
          direction,
          screenPosition: this.coordinateTransformer.worldToScreen(midpoint),
          positiveLength: halfLength,
          negativeLength: halfLength,
        });
      }
    }

    // Clear the polygon preview and depth segmentation state
    this.polygonRenderer.clearPreviewMesh();
    this.polygonRenderer.clearPolygonRendering();
    this.clearRegionPolygonPreview();
    this.clearRegionSelection();
    this.clearAutoPromptVisuals();
    this.regionPolygonCache.clear();
    this.autoPrompts = [];
    this.regionGraph = null;

    // Update shape preview with the new prompts
    this.updateShapePreview();

    // Iteratively remove unconnected ("red") prompts until the shape is valid.
    // After ShapeBuilder runs, prompts missing a positive or negative connection
    // don't participate in the shape and should be pruned.
    this.pruneUnconnectedPrompts();
  }

  /**
   * Iteratively remove unconnected prompts (missing positive or negative connection)
   * one at a time, rebuilding the shape each time, until all remaining prompts
   * are fully connected or fewer than 3 remain.
   */
  private pruneUnconnectedPrompts(): void {
    const MAX_ITERATIONS = 50; // Safety limit to avoid infinite loops
    for (let i = 0; i < MAX_ITERATIONS; i++) {
      const regularPrompts = this.linePrompts.filter((p) => !p.isTemporary);
      if (regularPrompts.length < 3) break;

      // Find all unconnected prompts (red prompts), pick the smallest one first
      const unconnectedPrompts = regularPrompts.filter((p) => p.connections?.positive === undefined || p.connections?.negative === undefined);

      if (unconnectedPrompts.length === 0) break; // All prompts are fully connected — shape is valid

      // Sort by total line length (smallest first) so we remove the least significant prompt
      const unconnected = unconnectedPrompts.reduce((smallest, p) => {
        const len = (p.positiveLength ?? 0) + (p.negativeLength ?? 0);
        const smallestLen = (smallest.positiveLength ?? 0) + (smallest.negativeLength ?? 0);
        return len < smallestLen ? p : smallest;
      });

      // Remove the unconnected prompt
      this.linePrompts = this.linePrompts.filter((p) => p.id !== unconnected.id);

      // Rebuild the shape with remaining prompts
      this.updateShapePreview();
    }
  }

  /**
   * Check if the mouse cursor is currently over an editable shape (rooms,
   * door/window strips, …). Delegated to the kind adapter so the right
   * primary-target type is checked per registration.
   */
  private isMouseOverRoom(): boolean {
    if (this.editingShape || this.linePrompts.length > 0) return false;
    return this.parentTool.kindAdapter.isMouseOverEditableShape({ viewer: this.viewer, persona: this.persona, mouse: this.parentTool.mouse, coordinateTransformer: this.coordinateTransformer, basePlane: this.currentBasePlane });
  }

  /**
   * Enter shape edit mode: converts the shape's edges to prompts for editing.
   * Currently shape is a room; broadens to EditableShape in step B.
   */
  enterShapeEditMode(shape: EditableShape): void {
    this.parentTool.undoRedo?.clear();

    // Get edges from the shape (this also hides the shape's visualization)
    const edges = shape.enterEditMode();

    this.editingShape = shape;
    this.parentTool.dispatchToolPhase('editing_room');
    // No frame resync needed — the provider reads `editingShape` live.

    // Snap edge midpoints onto the kind's base plane before turning them
    // into prompts. See ManualLineMode.enterShapeEditMode for the same
    // change — rooms flatten to floor, door/window project onto the wall.
    const snapped = this.parentTool.kindAdapter.snapPolygonToBasePlane(
      edges.map((edge) => edge.midpoint.clone()),
      { viewer: this.viewer, basePlane: this.currentBasePlane, shape },
    );
    this.linePrompts = edges.map((edge, i) => ({
      id: `prompt_${this.nextPromptId++}`,
      worldPoint: snapped[i],
      direction: edge.direction.clone(),
      screenPosition: this.coordinateTransformer.worldToScreen(snapped[i]),
    }));

    // Update shape preview with the new prompts
    this.updateShapePreview();

    // Run line detection to capture maps for density snapping during edit
    this.detectLines();
  }

  /**
   * Exit room edit mode with optional confirmation
   * @param confirm If true, updates the room with the new shape. If false, restores original.
   */
  private exitShapeEditMode(confirm: boolean): void {
    if (!this.editingShape) {
      return;
    }

    if (confirm && this.shapeRendererModule.currentShape && this.shapeRendererModule.currentShape.vertices.length >= 3) {
      const newPolygon = this.parentTool.kindAdapter.snapPolygonToBasePlane(
        this.shapeRendererModule.currentShape.vertices.map((v) => v.clone()),
        { viewer: this.viewer, basePlane: this.currentBasePlane, shape: this.editingShape },
      );

      this.editingShape.exitEditMode(true, newPolygon);
      this.parentTool.kindAdapter.commitEdit(this.viewer, this.editingShape, newPolygon);
    } else {
      this.editingShape.exitEditMode(false);
    }

    this.parentTool.kindAdapter.restoreViewAfterShape(this.editingShape, this.viewer);

    this.editingShape = null;
    this.parentTool.undoRedo?.clear();
    this.clearSelection();

    // Re-run segmentation so the region overlay is visible again
    if (this.linePrompts.length === 0) {
      this.runSegmentation();
      this.dispatchIdlePhase();
    }
  }

  isEditingShape(): boolean {
    return this.editingShape !== null;
  }

  getEditingShape(): EditableShape | null {
    return this.editingShape;
  }

  setEditingShape(shape: EditableShape | null): void {
    this.editingShape = shape;
  }

  // ========== LineToolModeCallbacks ==========

  getNextPromptId(): string {
    return `prompt_${this.nextPromptId++}`;
  }

  isPromptSphereDragging(): boolean {
    return this.promptSphereDrag !== null;
  }

  addPrompt(prompt: LinePrompt): void {
    this.linePrompts.push(prompt);
  }

  /**
   * Customize the area selector label to show "Align" message
   */
  private customizeAreaSelectorLabel(): void {
    const label = this.areaSelector.getAreaLabel();
    if (label) {
      this.areaSelector.setUpdateAreaLabelOverride((mouse: THREE.Vector2) => {
        // Update label text to show deletion info
        const selectionStart = this.areaSelector.getSelectionStart();
        if (selectionStart && label) {
          if (this.rightDragStart) {
            // Right-drag: deletion mode - use deletion manager for highlighting and validation
            const { selectedPrompts, validation } = this.deletionManager.updateSelectionHighlight(this.linePrompts, selectionStart, mouse, this.hoveredPromptId);

            const count = selectedPrompts.length;
            if (count > 0 && !validation.valid) {
              // Invalid deletion - show error message in red
              label.textContent = validation.message;
              label.style.backgroundColor = 'rgba(200, 0, 0, 0.8)';
            } else {
              // Valid deletion or no prompts - show nothing
              label.textContent = '';
              label.style.backgroundColor = 'transparent';
            }
          }
        }
      });
    }
  }

  /**
   * Rectangle-only kinds (door/window) lock prompt insertion/deletion once
   * the shape's four sides exist — post-segmentation prompts or an edited
   * shape — because extra/missing vertices would break the rectangle.
   */
  private rectangleLocked(): boolean {
    if (this.parentTool.kindAdapter.rectangleOnly !== true) return false;
    return this.editingShape !== null || this.linePrompts.length >= 4;
  }

  /**
   * Delete all line prompts whose worldPoint (midpoint/sphere) falls inside a screen-space box.
   * Implements smart deletion: when consecutive prompts are removed, connects the boundary
   * prompts with a new edge to maintain a valid shape.
   */
  private deletePromptsInBox(start: THREE.Vector2, end: THREE.Vector2): void {
    if (this.rectangleLocked()) return;
    // Snapshot before deletion so Ctrl+Z restores the pre-delete state
    this.parentTool.pushUndoSnapshot();

    // Use deletion manager to execute the delete with validation
    const newPrompts = this.deletionManager.executeDelete(this.linePrompts, start, end, this.hoveredPromptId, false, (prompts) => {
      this.linePrompts = prompts;
      this.updateShapePreview();
    });

    if (newPrompts === null) {
      // Deletion was invalid or no prompts selected — pop the snapshot we just pushed
      this.parentTool.undoRedo?.popState();
      return;
    }

    this.linePrompts = newPrompts;

    // The shape will automatically reconnect through the ShapeBuilder when we call updateShapePreview
    this.updateShapePreview();

    // If all prompts were deleted, re-run segmentation
    if (this.linePrompts.length === 0) {
      this.runSegmentation();
    }
  }

  // ─── Undo / Redo (LineToolModeUndoRedo interface) ─────────────────────────

  captureUndoSnapshot(): PromptSnapshot {
    return {
      prompts: this.linePrompts.map((p) => ({
        id: p.id,
        worldPoint: p.worldPoint.clone(),
        direction: p.direction.clone(),
        screenPosition: p.screenPosition.clone(),
        positiveLength: p.positiveLength,
        negativeLength: p.negativeLength,
        isTemporary: p.isTemporary,
      })),
      nextPromptId: this.nextPromptId,
    };
  }

  restoreFromSnapshot(snapshot: PromptSnapshot): void {
    this.linePrompts = snapshot.prompts.map((s) => ({
      id: s.id,
      worldPoint: s.worldPoint.clone(),
      direction: s.direction.clone(),
      screenPosition: s.screenPosition.clone(),
      positiveLength: s.positiveLength,
      negativeLength: s.negativeLength,
      isTemporary: s.isTemporary,
    }));
    this.nextPromptId = snapshot.nextPromptId;

    this.updatePromptScreenPositions();
    this.updateShapePreview();

    // Also update temporary shape preview if there are temporary prompts
    if (this.linePrompts.some((p) => p.isTemporary)) {
      this.updateTemporaryShapePreview();
    } else {
      this.clearCutoffPreview();
    }

    if (this.linePrompts.length === 0) {
      this.clearShape();
      this.visualizer.clearPromptVisuals();
      this.lineRenderer.render(this.detectedLines);
      this.runSegmentation();
    }
  }

  prepareForUndoRedo(): void {
    this.firstClickPoint = null;
    this.visualizer.clearLinePreview();
  }

  // ─── Prompt sphere drag helpers (delegated to PromptDragHandler / DensitySnapper) ──

  private movePromptToMouse(mouse: THREE.Vector2): void {
    if (!this.promptSphereDrag) return;
    this.lastDragMouse = mouse.clone();
    const floorHeight = this.currentBasePlane?.origin.z ?? null;
    const camera = this.viewer.scene.getActiveCamera();
    const viewportWidth = this.viewer.renderArea.clientWidth;
    const viewportHeight = this.viewer.renderArea.clientHeight;

    // Call movePromptToMouse with detected lines for snap-to-line support.
    // `invertShiftFor90Snap` flips the shift bit for kinds where 90°
    // lock is the natural default (door/window: openings are axis-
    // aligned to the wall, so shift means "free-rotate"). For rooms
    // shift means "lock 90°" as before.
    //
    // Pure-geometry kinds (outline) opt out of detection — pass empty/null
    // maps so the drag handler skips snap-to-line + density alignment.
    // Kinds with `captureMapsForSnapping` keep the density maps flowing
    // (density snapping without detection — Wall Tracer room edits).
    const detectionOn = this.parentTool.kindAdapter.enableLineDetection !== false;
    const snapMapsOn = detectionOn || this.parentTool.kindAdapter.captureMapsForSnapping === true;
    const effectiveShift = this.parentTool.kindAdapter.invertShiftFor90Snap ? !this.shiftDown : this.shiftDown;
    const snapResult = this.promptDragHandler.movePromptToMouse(
      mouse,
      this.promptSphereDrag,
      this.linePrompts,
      floorHeight,
      snapMapsOn ? this.lineDetectionService.cachedDensityMap : null,
      snapMapsOn ? this.lineDetectionService.cachedPositionMap : null,
      effectiveShift,
      () => this.updateShapePreview(),
      detectionOn ? this.detectedLines : [],
      viewportWidth,
      viewportHeight,
      camera,
      this.ctrlDown,
      { perpendicularOnly: this.parentTool.kindAdapter.rectangleOnly === true },
    );

    // Optional adapter-level constraint snap (shared with ManualLineMode).
    // Off by default; adapters opt in via `enableConstraintSnap`.
    if (
      applyConstraintSnap(this.promptSphereDrag.prompt, {
        viewer: this.viewer,
        kindAdapter: this.parentTool.kindAdapter,
        basePlane: this.currentBasePlane,
        editingShape: this.editingShape,
        coordinateTransformer: this.coordinateTransformer,
        modifiers: { shiftDown: this.shiftDown, ctrlDown: this.ctrlDown },
        allPrompts: this.linePrompts,
        visuals: this.constraintSnapVisuals,
      })
    ) {
      this.updateShapePreview();
    }

    // Render snap target spheres on detected lines, highlighting the nearest one.
    // Skipped when detection is disabled for this kind (outline edits).
    if (detectionOn) {
      const nearestLineId = snapResult?.line.id ?? null;
      this.visualizer.renderSnapTargets(this.detectedLines, nearestLineId);
      if (this.densitySnapper.debugEnabled) {
        this.densitySnapper.renderDebug(this.viewer.scene.sceneDebug, floorHeight);
      } else {
        this.densitySnapper.clearDebug(this.viewer.scene.sceneDebug);
      }
    } else {
      this.visualizer.clearSnapTargets();
      this.densitySnapper.clearDebug(this.viewer.scene.sceneDebug);
    }
  }

  private cleanupEdgesAfterDrag(movedPrompt: LinePrompt): void {
    // Rectangle-only kinds keep fixed 90° corners — the collinear merge could
    // only ever fire erroneously and would destroy a side, so skip it.
    if (this.parentTool.kindAdapter.rectangleOnly) return;
    const floorHeight = this.currentBasePlane?.origin.z ?? null;
    this.linePrompts = this.promptDragHandler.cleanupEdgesAfterDrag(movedPrompt, this.linePrompts, floorHeight, (prompts) => {
      this.linePrompts = prompts;
      this.updateShapePreview();
    });
  }

  /**
   * Handle right-click to cancel two-click mode or remove prompts
   */
  onRightClick(event: MouseEvent): void {
    // Skip if a right-drag box just completed (contextmenu fires after mouseup)
    if (this.rightDragWasBox) {
      this.rightDragWasBox = false;
      return;
    }

    // Ctrl-refine right-click is handled by LineTool before delegation

    // Cancel prompt sphere drag — restore original position + neighbors
    if (this.promptSphereDrag) {
      // Discard the undo snapshot that was pushed at drag start
      this.parentTool.undoRedo?.popState();
      const { prompt, originalWorldPoint, originalDirection, originalPosLength, originalNegLength, neighborSnapshots } = this.promptSphereDrag;
      prompt.worldPoint.copy(originalWorldPoint);
      prompt.direction.copy(originalDirection);
      prompt.positiveLength = originalPosLength;
      prompt.negativeLength = originalNegLength;
      prompt.screenPosition = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
      for (const snap of neighborSnapshots) {
        snap.prompt.worldPoint.copy(snap.worldPoint);
        snap.prompt.screenPosition.copy(snap.screenPosition);
      }
      this.promptSphereDrag = null;
      this.dragBoxStart = null;
      this.visualizer.clearSnapTargets();
      this.constraintSnapVisuals.clear();
      // Re-enable orbital controls after drag ends (in case ctrl was held)
      this.viewer.controls.enabled = true;
      this.updateShapePreview();
      this.viewer.renderer.domElement.style.cursor = 'default';
      return;
    }

    // Cancel prompt line drag (box creation) if active
    if (this.promptLineDrag) {
      this.promptLineDrag = null;
      this.snappedBoxEdges = null;
      this.dragBoxStart = null;
      this.visualizer.clearBoxPreview();
      this.viewer.controls.enabled = true;
      this.viewer.renderer.domElement.style.cursor = 'default';
      return;
    }

    // Cancel two-click mode if active
    if (this.firstClickPoint) {
      this.firstClickPoint = null;
      this.visualizer.clearLinePreview();
      this.showPreviewLines();
      this.viewer.renderer.domElement.style.cursor = 'default';
      return;
    }

    // Cancel drag box if active
    if (this.areaSelector.isSelecting()) {
      this.areaSelector.cancelSelection();
      this.dragBoxStart = null;
      return;
    }

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    const camera = this.viewer.scene.getActiveCamera();
    const is3D = camera.type === 'PerspectiveCamera';

    if (is3D) {
      // In 3D, right-click deletes a hovered prompt, otherwise undoes
      const hoveredPrompt = this.hitTester.findHoveredPromptSphere(mouse, this.linePrompts);
      if (hoveredPrompt) {
        this.parentTool.pushUndoSnapshot();
        const index = this.linePrompts.indexOf(hoveredPrompt);
        if (index !== -1) {
          this.linePrompts.splice(index, 1);
          this.updateShapePreview();
        }
      } else {
        this.parentTool.undoRedo?.undo();
      }
      return;
    }

    // Right-click on a temporary prompt removes it (2D only)
    const closestPrompt = this.hitTester.findClosestPrompt(mouse, this.linePrompts);
    if (closestPrompt && closestPrompt.isTemporary) {
      this.parentTool.pushUndoSnapshot();
      const index = this.linePrompts.indexOf(closestPrompt);
      if (index !== -1) {
        this.linePrompts.splice(index, 1);
        this.updateShapePreview();
      }
    }
  }

  /**
   * Update hover state for prompts and show isolation plane
   */
  private updatePromptHoverState(screenPoint: THREE.Vector2): void {
    const hoveredPrompt = this.hitTester.findHoveredPromptSphere(screenPoint, this.linePrompts);
    const newHoveredId = hoveredPrompt?.id || null;

    if (newHoveredId !== this.hoveredPromptId) {
      this.hoveredPromptId = newHoveredId;
    }
  }

  /**
   * Update isolation plane visibility for preview line hover
   */
  private updatePreviewLineHoverState(hoveredLine: DetectedLine | null): void {
    const is3D = this.viewer.scene.getActiveCamera().type === 'PerspectiveCamera';

    // Isolation plane only in 3D — in x-ray it covers the whole floor
    if (hoveredLine && is3D) {
      const midpoint = this.lineRenderer.getLineMidpoint(hoveredLine);
      const floorHeight = this.currentBasePlane?.origin.z;
      this.visualizer.showIsolationPlane(midpoint, hoveredLine.direction, floorHeight);
    } else if (!this.hoveredPromptId) {
      this.visualizer.hideIsolationPlane();
    }
  }

  /**
   * Show preview lines again (detected lines) - used when exiting manual drawing mode
   */
  private showPreviewLines(): void {
    // Unhide all lines and re-render
    for (const line of this.detectedLines) {
      line.isHidden = false;
    }
    this.renderDetectedLinesSafe();
  }

  /**
   * Render detected lines. Lines render in world space using their captured
   * world corners, so they remain valid even if camera has moved.
   */
  private renderDetectedLinesSafe(): void {
    this.lineRenderer.render(this.detectedLines);
  }

  /**
   * Get mouse position relative to renderArea
   */
  private getMousePosition(event: MouseEvent): THREE.Vector2 | null {
    const rect = this.viewer.renderArea.getBoundingClientRect();
    return new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
  }

  /**
   * Handle line click - create a persistent prompt at the click location
   */
  private handleLineClick(line: DetectedLine, event: MouseEvent): void {
    // Get click position in screen space
    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    // Check if this should be a temporary prompt (when a shape already exists)
    const hasCurrentShape = this.shapeRendererModule.currentShape !== null;
    const isTemporaryPrompt = hasCurrentShape;

    this.parentTool.pushUndoSnapshot();

    // Project click position onto line segment in world space
    // Use the line's world coordinates directly (more accurate than screenToWorld)
    const worldPoint = this.hitTester.closestPointOnLineFromScreen(line, mouse);

    // Calculate lengths in positive and negative directions
    // The direction vector points from start to end, so:
    // - positive direction goes towards line.end
    // - negative direction goes towards line.start
    const toStart = new THREE.Vector3().subVectors(line.start, worldPoint);
    const toEnd = new THREE.Vector3().subVectors(line.end, worldPoint);

    // Project onto direction to get signed distances
    const distToStartAlongDir = toStart.dot(line.direction); // negative value (towards start)
    const distToEndAlongDir = toEnd.dot(line.direction); // positive value (towards end)

    // positiveLength = distance in positive direction (towards end)
    // negativeLength = distance in negative direction (towards start, stored as positive value)
    const positiveLength = Math.max(0, distToEndAlongDir);
    const negativeLength = Math.max(0, -distToStartAlongDir);

    // Create persistent LinePrompt with world position, direction, and lengths
    const prompt: LinePrompt = {
      id: `prompt_${this.nextPromptId++}`,
      worldPoint,
      direction: line.direction.clone(),
      screenPosition: mouse.clone(),
      positiveLength,
      negativeLength,
      isTemporary: isTemporaryPrompt ? true : undefined,
    };
    this.linePrompts.push(prompt);

    // Always update shape preview (updates connection points and prompts visuals)
    this.updateShapePreview();
  }

  /**
   * Create a prompt perpendicular to the target prompt line.
   * The new prompt is placed at the midpoint between cursor and snap point,
   * with direction perpendicular to the target line.
   * The combined edge lengths equal the distance from cursor to the line.
   */
  private createPerpendicularSnapPrompt(): void {
    if (!this.perpendicularSnapTarget) return;

    // Check if this should be a temporary prompt (when a shape already exists)
    const hasCurrentShape = this.shapeRendererModule.currentShape !== null;
    const isTemporaryPrompt = hasCurrentShape;

    this.parentTool.pushUndoSnapshot();

    const { snapPoint, previewEndPoint, distance } = this.perpendicularSnapTarget;

    // Calculate perpendicular direction (from snap point to cursor position)
    const perpendicularDir = new THREE.Vector3().subVectors(previewEndPoint, snapPoint).normalize();

    // Place the prompt at the midpoint between snap point and cursor
    const midpoint = new THREE.Vector3().addVectors(snapPoint, previewEndPoint).multiplyScalar(0.5);

    // The combined edge lengths should equal the distance from cursor to line
    // Since prompt is at midpoint, each direction extends half the distance
    const halfDistance = distance / 2;

    const prompt: LinePrompt = {
      id: `prompt_${this.nextPromptId++}`,
      worldPoint: midpoint,
      direction: perpendicularDir,
      screenPosition: this.coordinateTransformer.worldToScreen(midpoint),
      positiveLength: halfDistance,
      negativeLength: halfDistance,
      isTemporary: isTemporaryPrompt ? true : undefined,
    };
    this.linePrompts.push(prompt);

    // Clear perpendicular snap state
    this.perpendicularSnapTarget = null;
    this.visualizer.clearLinePreview();

    // Update shape preview
    this.updateShapePreview();
  }

  /**
   * Create a line prompt from two manually clicked points.
   * All manually drawn lines (shift+click two-click mode) become temporary prompts
   * when there's an existing shape. Only pull-out and mask conversion create main prompts.
   */
  private createLinePromptFromTwoPoints(point1: THREE.Vector3, point2: THREE.Vector3): void {
    // All manually drawn lines become temporary prompts when a shape exists
    const hasCurrentShape = this.shapeRendererModule.currentShape !== null;
    const isTemporaryPrompt = hasCurrentShape;

    // Push undo snapshot for all prompt creations (including temporary)
    this.parentTool.pushUndoSnapshot();

    // Calculate direction from point1 to point2
    const direction = new THREE.Vector3().subVectors(point2, point1).normalize();

    // Use midpoint as the prompt location, then snap onto the kind's
    // base plane (rooms → floor Z, door/window → wall plane). Pre-adapter
    // this was a hardcoded `midpoint.z = floorHeight` which broke
    // vertical (door/window) prompts.
    const midpoint = new THREE.Vector3().addVectors(point1, point2).multiplyScalar(0.5);
    const [snappedMidpoint] = this.parentTool.kindAdapter.snapPolygonToBasePlane([midpoint], { viewer: this.viewer, basePlane: this.currentBasePlane, shape: this.editingShape });
    midpoint.copy(snappedMidpoint);

    // Calculate half-length (distance from midpoint to each end is the same)
    const halfLength = point1.distanceTo(point2) / 2;

    // Create prompt with equal lengths in both directions
    const prompt: LinePrompt = {
      id: `prompt_${this.nextPromptId++}`,
      worldPoint: midpoint,
      direction,
      screenPosition: this.coordinateTransformer.worldToScreen(midpoint),
      positiveLength: halfLength,
      negativeLength: halfLength,
    };

    if (isTemporaryPrompt) {
      prompt.isTemporary = true;
    }

    this.linePrompts.push(prompt);

    if (isTemporaryPrompt) {
      this.parentTool.dispatchToolPhase('drawing_walls');
      this.updateTemporaryShapePreview();
    } else {
      this.updateShapePreview();
    }
  }

  /**
   * Update the line preview visualization from first click point to current mouse position
   */
  private updateLinePreview(mouse: THREE.Vector2): void {
    if (!this.firstClickPoint) {
      this.visualizer.clearLinePreview();
      return;
    }

    const currentWorldPoint = this.coordinateTransformer.screenToWorld(mouse);
    if (currentWorldPoint) {
      // `screenToWorld` already unprojects onto `viewerMode.associatedPlane`,
      // so the point lives on the active base plane (floor for rooms,
      // vertical wall plane for door/window phase-2). Pre-base-plane this
      // forced `z = firstClickPoint.z` to defend against floor-Z drift,
      // which locked the preview horizontal on a wall plane.
      this.visualizer.renderLinePreview(this.firstClickPoint, currentWorldPoint);
    }
  }

  /**
   * Create a box (rectangular extrusion) from a prompt line drag operation.
   * Delegates to BoxExtrusionHandler for the actual box creation logic.
   */
  private createBoxFromPromptLineDrag(drag: { prompt: LinePrompt; startPoint: THREE.Vector3; side: 'positive' | 'negative' }, endWorldPoint: THREE.Vector3, snappedEdges?: Array<[THREE.Vector3, THREE.Vector3]> | null): void {
    // Check if we currently have a valid shape (before the box operation)
    const regularPromptsBefore = this.linePrompts.filter((p) => !p.isTemporary);
    const shapeBefore = regularPromptsBefore.length >= 3 ? this.shapeBuilder.createShapeFromPrompts(regularPromptsBefore) : null;
    const hadValidShape = shapeBefore !== null && shapeBefore.vertices.length >= 3 && shapeBefore.vertices.length === regularPromptsBefore.length;

    // Deep snapshot: createBoxFromDrag may mutate neighbor prompts in-place
    // (via adjustNeighborForCollapsedLeg), so we need to save their mutable state
    const promptsBefore = [...this.linePrompts];
    const promptSnapshots = this.linePrompts.map((p) => ({
      prompt: p,
      worldPoint: p.worldPoint.clone(),
      screenPosition: p.screenPosition.clone(),
    }));
    const savedNextPromptId = this.nextPromptId;

    const result = this.boxExtrusionHandler.createBoxFromDrag(
      drag,
      endWorldPoint,
      this.linePrompts,
      () => `prompt_${this.nextPromptId++}`,
      snappedEdges ?? null,
      this.lineDetectionService.cachedDensityMap,
      this.lineDetectionService.cachedPositionMap,
      this.currentBasePlane?.origin.z ?? null,
      drag.prompt.isTemporary,
    );
    if (!result) return;

    // Remove the original prompt and add the new ones
    if (result.originalPromptIndex !== -1) {
      this.linePrompts.splice(result.originalPromptIndex, 1);
    }
    this.linePrompts.push(...result.newPrompts);

    // If we had a valid shape before, verify the box didn't break it
    const allowInvalid = this.debugWidget?.allowInvalidBoxShape ?? false;
    if (hadValidShape && !drag.prompt.isTemporary && !allowInvalid) {
      const regularPromptsAfter = this.linePrompts.filter((p) => !p.isTemporary);
      const shapeAfter = this.shapeBuilder.createShapeFromPrompts(regularPromptsAfter);
      const isStillValid = shapeAfter !== null && shapeAfter.vertices.length >= 3 && shapeAfter.vertices.length === regularPromptsAfter.length;

      if (!isStillValid) {
        // Revert array and restore mutated prompt positions
        this.linePrompts = promptsBefore;
        this.nextPromptId = savedNextPromptId;
        for (const snapshot of promptSnapshots) {
          snapshot.prompt.worldPoint.copy(snapshot.worldPoint);
          snapshot.prompt.screenPosition.copy(snapshot.screenPosition);
        }
        this.updateShapePreview();

        this.viewer.scene.dispatchEvent({
          type: 'box_breaks_shape_notification',
        });
        return;
      }
    }

    this.updateShapePreview();
  }

  /**
   * Update screen positions of all prompts (called on camera move)
   */
  private updatePromptScreenPositions(): void {
    for (const prompt of this.linePrompts) {
      prompt.screenPosition = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
    }
  }

  /**
   * Update shape preview from line prompts using ray intersection algorithm.
   * Always updates connection points on prompts, even if no valid shape exists yet.
   */
  updateShapePreview(): void {
    this.shapeRendererModule.clear();

    // Get only non-temporary prompts for the main shape
    const regularPrompts = this.linePrompts.filter((p) => !p.isTemporary);

    // Trigger line detection when first prompt is added (editing mode starts)
    // This switches from segmentation mode to line detection mode
    if (regularPrompts.length === 1) {
      this.detectLines();
    }

    // Use the new ray-based algorithm with non-temporary line prompts
    // This will update connection points on all regular prompts
    const shape = this.shapeBuilder.createShapeFromPrompts(regularPrompts);

    // Update prompt visuals to reflect new connection points (show all prompts).
    // Handles render in the viewer UI scene, so they draw on top of the shape.
    this.visualizer.updatePromptVisuals(this.linePrompts, this.hoveredPromptId);

    if (shape && shape.vertices.length >= 3) {
      this.shapeRendererModule.render(shape);
      if (!this.editingShape) {
        const is3D = this.viewer.scene.getActiveCamera().type === 'PerspectiveCamera';
        this.parentTool.dispatchToolPhase(is3D ? 'confirm_room' : 'editing_shape');
      }
    } else if (regularPrompts.length > 0) {
      this.dispatchIdlePhase();
    }

    // Also update temporary shape preview if there are temporary prompts
    const hasTemporaryPrompts = this.linePrompts.some((p) => p.isTemporary);
    if (hasTemporaryPrompts) {
      this.parentTool.dispatchToolPhase('drawing_walls');
      this.updateTemporaryShapePreview();
    } else {
      // Clear temporary visuals when no temporary prompts exist
      this.temporaryShapeRenderer.clear();
      this.clearCutoffPreview();
    }
  }

  private clearShape(): void {
    this.shapeRendererModule.clear();
  }

  /**
   * Update temporary shape preview for shape extension mode.
   * Builds shape from temporary prompts that connect to the main shape's polygon.
   * Renders the cutoff section (edges that will be removed) in red.
   */
  private updateTemporaryShapePreview(): void {
    // Clear cutoff preview
    this.clearCutoffPreview();

    // Get temporary prompts from the unified linePrompts array
    const temporaryPrompts = this.linePrompts.filter((p) => p.isTemporary);

    if (temporaryPrompts.length === 0) {
      return;
    }

    // Get current main shape vertices
    const mainShapeVertices = this.shapeRendererModule.currentShape?.vertices || [];
    if (mainShapeVertices.length < 3) {
      return;
    }

    // Build temporary shape with external polygon support
    const result = this.temporaryShapeBuilder.createShapeWithExternalPolygon(temporaryPrompts, mainShapeVertices);

    // Remap targetPromptIndex from temp-subarray indices to full linePrompts indices.
    // createShapeWithExternalPolygon sets indices relative to the temporaryPrompts array,
    // but drag/recenter logic uses them to index into the full this.linePrompts array.
    for (const tempPrompt of temporaryPrompts) {
      if (!tempPrompt.connections) continue;
      for (const side of ['positive', 'negative'] as const) {
        const conn = tempPrompt.connections[side];
        if (!conn || conn.targetPromptIndex < 0) continue; // Skip external connections (-1)
        const targetTempPrompt = temporaryPrompts[conn.targetPromptIndex];
        if (targetTempPrompt) {
          const fullIdx = this.linePrompts.indexOf(targetTempPrompt);
          if (fullIdx >= 0) {
            conn.targetPromptIndex = fullIdx;
          }
        }
      }
    }

    // If we have exactly 2 connections, render the cutoff section (red filled polygon)
    if (result?.connections.length === 2) {
      // Always render the cutoff preview when we have 2 connections
      // Use the connection points directly if shape vertices aren't available
      const tempVertices = result.shape?.vertices || [result.connections[0].point.clone(), result.connections[1].point.clone()];
      this.renderCutoffPreview(mainShapeVertices, result.connections, tempVertices);
    }

    // Update the main visualizer with all prompts (it will render both regular and temporary)
    this.visualizer.updatePromptVisuals(this.linePrompts, this.hoveredPromptId);
  }

  /**
   * Render the cutoff section of the original polygon that will be removed on merge.
   * Shows the shortest path between connections with:
   * - Red line on original polygon edges being removed
   * - Green fill if extending outside, red fill if cutting inside
   */
  private renderCutoffPreview(mainShapeVertices: THREE.Vector3[], connections: Array<{ edgeIndex: number; point: THREE.Vector3 }>, tempShapeVertices: THREE.Vector3[]): void {
    if (tempShapeVertices.length < 2) return;

    // Sort connections by edge index, and by position along edge if on same edge
    const [conn1, conn2] = this.sortConnectionsByEdgePosition(connections, mainShapeVertices);

    // Calculate both paths and pick the shorter one
    const path1 = this.getPathBetweenConnections(mainShapeVertices, conn1, conn2, true);
    const path2 = this.getPathBetweenConnections(mainShapeVertices, conn1, conn2, false);

    const length1 = this.calculatePathLength(path1);
    const length2 = this.calculatePathLength(path2);

    // Use the shorter path as the cutoff (to be removed)
    const forwardIsShorter = length1 <= length2;
    const cutoffVertices = forwardIsShorter ? path1 : path2;

    if (cutoffVertices.length < 2) return;

    // Determine if temp shape extends inside or outside the original polygon
    // by checking if the centroid of the temp shape is inside the original polygon
    const tempCentroid = this.calculateCentroid(tempShapeVertices);
    const isInside = this.isPointInPolygon(tempCentroid, mainShapeVertices);

    // Green if extending outside, red if cutting inside
    const fillColor = isInside ? 0xff4444 : 0x44ff44;

    // Create the visual group
    this.cutoffPreviewGroup = new THREE.Group();

    // Use first vertex as local origin for numerical stability
    const localOrigin = cutoffVertices[0].clone();
    this.cutoffPreviewGroup.position.copy(localOrigin);

    // Convert cutoff path to local coordinates
    const localCutoffVertices = cutoffVertices.map((v) => v.clone().sub(localOrigin));

    // Build the closed cutoff polygon: cutoff path + temp shape path back
    // Cutoff path goes: conn1 -> ... -> conn2
    // We need temp shape to go: conn2 -> ... -> conn1 to close the loop
    const cutoffPolygonVertices = [...localCutoffVertices];

    // Determine which direction the temp shape vertices are in
    // tempShapeVertices[0] is the first external connection, tempShapeVertices[last] is the second
    // These may not match conn1/conn2 (sorted by edgeIndex)
    const tempFirst = tempShapeVertices[0];

    // Check if temp shape starts closer to conn1 or conn2
    const tempFirstToConn1 = tempFirst.distanceTo(conn1.point);
    const tempFirstToConn2 = tempFirst.distanceTo(conn2.point);
    const tempStartsAtConn1 = tempFirstToConn1 < tempFirstToConn2;

    // Convert temp vertices to local coordinates
    const tempLocalVertices = tempShapeVertices.map((v) => v.clone().sub(localOrigin));

    // Cutoff ends at conn2, need temp to go from conn2 back to conn1
    if (tempStartsAtConn1) {
      // Temp goes conn1 -> conn2, reverse it to get conn2 -> conn1
      cutoffPolygonVertices.push(...tempLocalVertices.slice().reverse());
    } else {
      // Temp goes conn2 -> conn1, use as-is
      cutoffPolygonVertices.push(...tempLocalVertices);
    }

    if (cutoffPolygonVertices.length >= 3) {
      // Build the fill mesh ON the base plane (which may be tilted — surfaces,
      // door/window walls). We triangulate in the plane's local (u, v) frame so
      // the topology is correct, but emit the real 3D vertex positions so the
      // mesh lies exactly on the plane instead of being flattened to world XY at
      // an average Z (the old room-only behavior). Falls back to world XY when
      // no frame is installed (degenerate / no selection).
      const groupOrigin = this.cutoffPreviewGroup.position;
      const frame = this.coordinateTransformer.getBasePlaneFrame();
      const to2D = (v: THREE.Vector3): THREE.Vector2 => (frame ? new THREE.Vector2(v.clone().sub(groupOrigin).dot(frame.u), v.clone().sub(groupOrigin).dot(frame.v)) : new THREE.Vector2(v.x, v.y));

      // `cutoffPolygonVertices` are already local to the group origin; lift back
      // to world for the (u, v) projection, then triangulate that 2D outline.
      const worldVerts = cutoffPolygonVertices.map((v) => v.clone().add(groupOrigin));
      const shape2D = new THREE.Shape(worldVerts.map(to2D));
      const shapeGeom = new THREE.ShapeGeometry(shape2D);
      const index = shapeGeom.getIndex();

      // Re-emit positions in the group-local 3D frame (the vertices, in order,
      // are the shape outline) so faces sit on the plane.
      const positions: number[] = [];
      for (const v of cutoffPolygonVertices) positions.push(v.x, v.y, v.z);
      const fillGeometry = new THREE.BufferGeometry();
      fillGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      if (index) fillGeometry.setIndex(index);
      fillGeometry.computeVertexNormals();

      const fillMaterial = new THREE.MeshBasicMaterial({
        color: fillColor,
        transparent: true,
        opacity: 0.3,
        side: THREE.DoubleSide,
        depthTest: true,
        depthWrite: false,
      });

      const fillMesh = new THREE.Mesh(fillGeometry, fillMaterial);
      fillMesh.renderOrder = 996;
      this.cutoffPreviewGroup.add(fillMesh);
    }

    // Render red line along the cutoff path (original polygon edges being removed)
    // This line is always red since these edges are always being removed
    if (localCutoffVertices.length >= 2) {
      const positions: number[] = [];
      for (const vertex of localCutoffVertices) {
        positions.push(vertex.x, vertex.y, vertex.z);
      }

      const lineGeometry = new LineGeometry();
      lineGeometry.setPositions(positions);

      const lineMaterial = new LineMaterial({
        color: 0xff0000, // Bright red
        linewidth: 6, // Thicker line
        resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
        depthTest: false, // Render on top
        transparent: true,
        opacity: 1.0,
      });

      const line = new Line2(lineGeometry, lineMaterial);
      line.renderOrder = 1001; // Higher render order
      line.computeLineDistances();
      this.cutoffPreviewGroup.add(line);
    }

    // Add to scene
    this.planePreviewScene.add(this.cutoffPreviewGroup);
  }

  /**
   * Get the path of vertices between two connection points on the polygon.
   *
   * An edge with index i connects vertex[i] to vertex[(i+1) % n].
   * conn1 and conn2 are points ON edges (between vertices).
   *
   * After sorting by edgeIndex: conn1.edgeIndex <= conn2.edgeIndex
   *
   * @param forward - if true, walk forward (increasing indices) from conn1 to conn2
   * @param conn1 - connection on edge with lower or equal index (after sorting)
   * @param conn2 - connection on edge with higher or equal index (after sorting)
   */
  private getPathBetweenConnections(vertices: THREE.Vector3[], conn1: { edgeIndex: number; point: THREE.Vector3 }, conn2: { edgeIndex: number; point: THREE.Vector3 }, forward: boolean): THREE.Vector3[] {
    const path: THREE.Vector3[] = [];
    const n = vertices.length;

    // Special case: both connections on the same edge
    if (conn1.edgeIndex === conn2.edgeIndex) {
      if (forward) {
        // Forward = direct connection (just the two points)
        path.push(conn1.point.clone());
        path.push(conn2.point.clone());
      } else {
        // Backward = go all the way around
        path.push(conn1.point.clone());
        // Go backward from conn1.edgeIndex to 0
        for (let i = conn1.edgeIndex; i >= 0; i--) {
          path.push(vertices[i].clone());
        }
        // Go from n-1 back down, but skip nothing since we need to go all the way around
        for (let i = n - 1; i > conn2.edgeIndex; i--) {
          path.push(vertices[i].clone());
        }
        path.push(conn2.point.clone());
      }
      return path;
    }

    if (forward) {
      // Forward path: conn1 -> vertices between -> conn2
      // Edge conn1.edgeIndex connects vertex[conn1.edgeIndex] to vertex[conn1.edgeIndex + 1]
      // So the first vertex after conn1.point in forward direction is vertex[conn1.edgeIndex + 1]
      path.push(conn1.point.clone());

      // Add vertices from (conn1.edgeIndex + 1) up to and including conn2.edgeIndex
      for (let i = conn1.edgeIndex + 1; i <= conn2.edgeIndex; i++) {
        path.push(vertices[i].clone());
      }

      path.push(conn2.point.clone());
    } else {
      // Backward path: conn1 -> wrap around -> conn2
      // The first vertex after conn1.point in backward direction is vertex[conn1.edgeIndex]
      path.push(conn1.point.clone());

      // Go backward from conn1.edgeIndex down to 0
      for (let i = conn1.edgeIndex; i >= 0; i--) {
        path.push(vertices[i].clone());
      }

      // Wrap around: go from vertex[n-1] down to vertex[conn2.edgeIndex + 1]
      for (let i = n - 1; i > conn2.edgeIndex; i--) {
        path.push(vertices[i].clone());
      }

      path.push(conn2.point.clone());
    }

    return path;
  }

  /**
   * Calculate the total length of a path of vertices.
   */
  private calculatePathLength(vertices: THREE.Vector3[]): number {
    let length = 0;
    for (let i = 0; i < vertices.length - 1; i++) {
      length += vertices[i].distanceTo(vertices[i + 1]);
    }
    return length;
  }

  /**
   * Sort connections by edge index, and by position along the edge if on the same edge.
   * This ensures consistent ordering when both connections are on the same edge.
   */
  private sortConnectionsByEdgePosition(connections: Array<{ edgeIndex: number; point: THREE.Vector3 }>, vertices: THREE.Vector3[]): [{ edgeIndex: number; point: THREE.Vector3 }, { edgeIndex: number; point: THREE.Vector3 }] {
    const [a, b] = connections;

    if (a.edgeIndex !== b.edgeIndex) {
      // Different edges - sort by edge index
      return a.edgeIndex < b.edgeIndex ? [a, b] : [b, a];
    }

    // Same edge - sort by position along the edge
    const edgeStart = vertices[a.edgeIndex];
    const distA = edgeStart.distanceTo(a.point);
    const distB = edgeStart.distanceTo(b.point);

    return distA < distB ? [a, b] : [b, a];
  }

  /**
   * Clear the cutoff preview visual.
   */
  private clearCutoffPreview(): void {
    if (this.cutoffPreviewGroup) {
      this.planePreviewScene.remove(this.cutoffPreviewGroup);
      this.cutoffPreviewGroup.traverse((obj) => {
        if (obj instanceof THREE.Mesh || obj instanceof Line2) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((mat) => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      this.cutoffPreviewGroup = null;
    }
  }

  /**
   * Check if temporary prompts can be merged (form a valid loop with exactly 2 connections).
   */
  private canMergeTemporaryPrompts(): boolean {
    const mainShape = this.shapeRendererModule.currentShape;
    if (!mainShape || mainShape.vertices.length < 3) return false;

    const temporaryPrompts = this.linePrompts.filter((p) => p.isTemporary);
    if (temporaryPrompts.length === 0) return false;

    const result = this.temporaryShapeBuilder.createShapeWithExternalPolygon(temporaryPrompts, mainShape.vertices);
    return result !== null && result.connections.length === 2;
  }

  /**
   * Merge temporary shape into the main shape.
   * Called when user clicks on the main shape while having temporary prompts.
   *
   * Strategy:
   * 1. Build the merged polygon vertices (kept edges from original + temp shape edges)
   * 2. Convert each edge of that polygon into a line prompt
   * 3. Replace all current prompts with these new prompts
   */
  private mergeTemporaryShape(): void {
    const mainShape = this.shapeRendererModule.currentShape;
    if (!mainShape || mainShape.vertices.length < 3) return;

    // Get temporary prompts from the unified array
    const temporaryPrompts = this.linePrompts.filter((p) => p.isTemporary);
    if (temporaryPrompts.length === 0) return;

    // Get connection info to determine which edges are being cut off
    const result = this.temporaryShapeBuilder.createShapeWithExternalPolygon(temporaryPrompts, mainShape.vertices);

    if (!result || result.connections.length !== 2) {
      console.warn('[LineTool] Cannot merge: need exactly 2 connection points');
      return;
    }

    // Push undo snapshot before merge
    this.parentTool.pushUndoSnapshot();

    // Build the merged polygon vertices
    const mergedVertices = this.buildMergedPolygonVertices(mainShape.vertices, result.connections, result.shape?.vertices || []);

    // Convert merged polygon edges to line prompts (all as non-temporary)
    this.linePrompts = this.polygonToPrompts(mergedVertices);

    // Clear temporary state
    this.temporaryShapeRenderer.clear();
    this.clearCutoffPreview();

    // Rebuild unified shape using the shape builder
    this.updateShapePreview();
  }

  /**
   * Build the merged polygon vertices by combining the kept edges of the original
   * polygon (the non-red path) with the temporary shape edges.
   *
   * Uses the SAME logic as renderCutoffPreview to determine which path is kept:
   * - Sort connections by edgeIndex
   * - Calculate paths between sorted conn1 and conn2
   * - SHORTER path = cutoff (red line, removed)
   * - LONGER path = kept
   *
   * Result = kept path + temp shape (properly oriented to close the loop)
   */
  private buildMergedPolygonVertices(mainVertices: THREE.Vector3[], connections: Array<{ edgeIndex: number; point: THREE.Vector3 }>, tempShapeVertices: THREE.Vector3[]): THREE.Vector3[] {
    if (tempShapeVertices.length < 2 || connections.length !== 2) {
      return [...mainVertices];
    }

    // Sort connections by edgeIndex, and by position along edge if on same edge
    const [conn1, conn2] = this.sortConnectionsByEdgePosition(connections, mainVertices);

    // Calculate both paths between sorted connections (same as renderCutoffPreview)
    const forwardPath = this.getPathBetweenConnections(mainVertices, conn1, conn2, true);
    const backwardPath = this.getPathBetweenConnections(mainVertices, conn1, conn2, false);
    const forwardLength = this.calculatePathLength(forwardPath);
    const backwardLength = this.calculatePathLength(backwardPath);

    // SHORTER path = cutoff (red), LONGER path = kept
    const forwardIsShorter = forwardLength <= backwardLength;
    const keptPath = forwardIsShorter ? backwardPath : forwardPath;

    // keptPath goes from conn1 -> ... -> conn2
    // We need to combine it with tempShapeVertices to form a closed loop

    // Determine temp shape orientation relative to sorted connections
    const tempFirst = tempShapeVertices[0];
    const tempStartsAtConn1 = tempFirst.distanceTo(conn1.point) < tempFirst.distanceTo(conn2.point);

    // Build merged polygon: keptPath + tempShape (in correct order to close loop)
    // keptPath: conn1 -> ... -> conn2
    // We need tempShape to go: conn2 -> ... -> conn1
    const mergedVertices: THREE.Vector3[] = [];

    // Add kept path vertices (conn1 -> conn2)
    for (const v of keptPath) {
      mergedVertices.push(v.clone());
    }

    // Add temp shape vertices going from conn2 -> conn1 (to close the loop)
    // Skip endpoints since they match keptPath endpoints
    if (tempStartsAtConn1) {
      // tempShape goes conn1 -> conn2, reverse to get conn2 -> conn1
      for (let j = tempShapeVertices.length - 2; j > 0; j--) {
        mergedVertices.push(tempShapeVertices[j].clone());
      }
    } else {
      // tempShape goes conn2 -> conn1, use as-is
      for (let j = 1; j < tempShapeVertices.length - 1; j++) {
        mergedVertices.push(tempShapeVertices[j].clone());
      }
    }

    return mergedVertices;
  }

  /**
   * Convert polygon vertices to line prompts.
   * Each edge becomes a prompt at its midpoint with direction along the edge.
   */
  private polygonToPrompts(vertices: THREE.Vector3[]): LinePrompt[] {
    const prompts: LinePrompt[] = [];
    for (let i = 0; i < vertices.length; i++) {
      const start = vertices[i];
      const end = vertices[(i + 1) % vertices.length];
      const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);

      // Full 3D direction along the edge. Earlier this normalized only the
      // XY components and forced z=0 — fine for rooms (edges live on a
      // horizontal floor plane so z is always 0 anyway), wrong for
      // door/window strips on a vertical wall plane where edges have a
      // real Z component. Forcing z=0 there collapses every diagonal/
      // vertical wall edge to a horizontal direction, so after the
      // temporary-shape merge runs on Enter all rebuilt prompts come out
      // horizontal regardless of the user's input.
      const direction = new THREE.Vector3().subVectors(end, start);
      const length = direction.length();
      if (length > 1e-10) {
        direction.divideScalar(length);
      } else {
        direction.set(1, 0, 0);
      }

      prompts.push({
        id: `merged_prompt_${this.nextPromptId++}`,
        worldPoint: midpoint,
        direction: direction,
        screenPosition: this.coordinateTransformer.worldToScreen(midpoint),
      });
    }
    return prompts;
  }

  /**
   * Calculate the centroid of a set of vertices.
   */
  private calculateCentroid(vertices: THREE.Vector3[]): THREE.Vector3 {
    const centroid = new THREE.Vector3();
    for (const v of vertices) {
      centroid.add(v);
    }
    centroid.divideScalar(vertices.length);
    return centroid;
  }

  /**
   * Check if a point is inside a polygon using ray casting algorithm.
   */
  private isPointInPolygon(point: THREE.Vector3, vertices: THREE.Vector3[]): boolean {
    let inside = false;
    for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
      const xi = vertices[i].x,
        yi = vertices[i].y;
      const xj = vertices[j].x,
        yj = vertices[j].y;

      if (yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  /**
   * Check if click is on the cutoff line (red line) for merge trigger.
   */
  private isClickOnMainShape(mouse: THREE.Vector2): boolean {
    return this.isNearCutoffLine(mouse);
  }

  /**
   * Check if mouse position is near the cutoff line (red line).
   * Used for both click detection and cursor change.
   */
  private isNearCutoffLine(mouse: THREE.Vector2): boolean {
    const shape = this.shapeRendererModule.currentShape;
    if (!shape) return false;

    const worldPoint = this.coordinateTransformer.screenToWorld(mouse);
    if (!worldPoint) return false;

    // Get the cutoff path (red line)
    const temporaryPrompts = this.linePrompts.filter((p) => p.isTemporary);
    if (temporaryPrompts.length === 0) return false;

    const result = this.temporaryShapeBuilder.createShapeWithExternalPolygon(temporaryPrompts, shape.vertices);

    if (!result || result.connections.length !== 2) return false;

    // Get the cutoff path vertices
    const [conn1, conn2] = result.connections.sort((a, b) => a.edgeIndex - b.edgeIndex);
    const path1 = this.getPathBetweenConnections(shape.vertices, conn1, conn2, true);
    const path2 = this.getPathBetweenConnections(shape.vertices, conn1, conn2, false);
    const length1 = this.calculatePathLength(path1);
    const length2 = this.calculatePathLength(path2);
    const cutoffVertices = length1 <= length2 ? path1 : path2;

    // Check if mouse is near any edge of the cutoff path (red line)
    const edgeTolerance = 0.1; // 10cm tolerance
    for (let i = 0; i < cutoffVertices.length - 1; i++) {
      const edgeStart = cutoffVertices[i];
      const edgeEnd = cutoffVertices[i + 1];

      if (this.isPointNearEdge(worldPoint, edgeStart, edgeEnd, edgeTolerance)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a point is near an edge (within tolerance).
   */
  private isPointNearEdge(point: THREE.Vector3, edgeStart: THREE.Vector3, edgeEnd: THREE.Vector3, tolerance: number): boolean {
    const edge = new THREE.Vector3().subVectors(edgeEnd, edgeStart);
    const edgeLength = edge.length();
    if (edgeLength < 0.001) return point.distanceTo(edgeStart) < tolerance;

    const edgeDir = edge.clone().normalize();
    const toPoint = new THREE.Vector3().subVectors(point, edgeStart);
    const projection = toPoint.dot(edgeDir);

    // Check if projection is within edge bounds (with some margin)
    if (projection < -tolerance || projection > edgeLength + tolerance) {
      return false;
    }

    // Calculate perpendicular distance
    const clampedProjection = Math.max(0, Math.min(edgeLength, projection));
    const projectedPoint = edgeStart.clone().add(edgeDir.multiplyScalar(clampedProjection));
    const distance = point.distanceTo(projectedPoint);

    return distance < tolerance;
  }

  /**
   * Finalize shape and convert to room
   */
  private finalizeShape(): void {
    this.parentTool.undoRedo?.clear();
    if (!this.shapeRendererModule.currentShape) {
      console.warn('[LineTool] No shape to finalize');
      return;
    }

    if (this.shapeRendererModule.currentShape.vertices.length < 3) {
      console.warn('[LineTool] Not enough vertices to create room');
      return;
    }

    // Check if we have a base plane
    if (!this.currentBasePlane) {
      console.warn('[LineTool] No floor plane selected, cannot create room');
      return;
    }

    this.createRoomFromShape(this.shapeRendererModule.currentShape);
  }

  /**
   * Materialize the drawn shape via the kind adapter's `commitNew`. The
   * adapter owns event-name + payload shape (rooms vs door/windows vs …).
   */
  private createRoomFromShape(shape: LineShape): void {
    // Only a valid base plane is required to commit. `captureZRange` is a
    // room-specific capture hint (floor+ceiling slab) and is intentionally null
    // for surfaces, so it must NOT gate the commit.
    if (!this.currentBasePlane) {
      console.warn('[LineTool] Cannot create shape without a base plane');
      return;
    }

    const polygonPoints = this.parentTool.kindAdapter.snapPolygonToBasePlane(
      shape.vertices.map((v) => v.clone()),
      { viewer: this.viewer, basePlane: this.currentBasePlane, shape: null },
    );

    this.parentTool.kindAdapter.commitNew(this.viewer, polygonPoints, {
      basePlane: this.currentBasePlane,
    });

    this.clearSelection();
    if (!this.parentTool.kindAdapter.autoCommitNewFromRegion) {
      // Chained: both passes call captureViewportMaps and overwrite the
      // coordinate-transformer encoding; running them in parallel desyncs the
      // sampling worker's regions from the active encoding.
      void this.runSegmentation().then(() => this.detectLines());
    }
  }

  /**
   * Clear current selection and all prompts
   */
  private clearSelection(): void {
    if (this.linePrompts.length > 0) {
      this.parentTool.pushUndoSnapshot();
    }
    this.linePrompts = [];
    this.firstClickPoint = null;
    this.visualizer.clearLinePreview();
    this.clearShape();
    this.visualizer.clearPromptVisuals();
    this.lineRenderer.render(this.detectedLines);

    // Clear temporary prompt state
    this.temporaryShapeRenderer.clear();
    this.clearCutoffPreview();

    this.dispatchIdlePhase();
  }

  /**
   * Handle keyboard shortcuts
   */
  onKeyDown(event: KeyboardEvent): void {
    // Undo/Redo is handled by BaseTool (LineTool shell) via keyboard interception

    // Edge-op controller owns its own Esc when it's running a pick/input
    // phase. Let it consume first.
    if (this.edgeOpController.isActive() && this.edgeOpController.onKeyDown(event)) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Escape') {
      // Ctrl-refine Escape is handled by LineTool before delegation
      // First cancel two-click mode if active
      if (this.firstClickPoint) {
        this.firstClickPoint = null;
        this.visualizer.clearLinePreview();
        this.showPreviewLines();
        this.viewer.renderer.domElement.style.cursor = 'default';
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // If editing a room, cancel and restore original
      if (this.editingShape) {
        this.exitShapeEditMode(false);
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Clear region selection if active (without converting)
      if (this.regionBrushManager.selectedRegionIds.size > 0) {
        this.cancelRegionSelection();
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Clear temporary prompts first if any exist
      const tempPrompts = this.linePrompts.filter((p) => p.isTemporary);
      if (tempPrompts.length > 0) {
        this.parentTool.pushUndoSnapshot();
        this.linePrompts = this.linePrompts.filter((p) => !p.isTemporary);
        this.temporaryShapeRenderer.clear();
        this.clearCutoffPreview();
        this.updateShapePreview();
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Then clear all prompts if any exist (before exiting the tool)
      if (this.linePrompts.length > 0) {
        this.clearSelection();
        // Re-run segmentation since camera may have moved while editing
        this.runSegmentation();
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // If nothing to cancel, let escape propagate (to exit tool)
    } else if (event.key === 'Enter') {
      // Convert selected regions to line prompts
      if (this.regionBrushManager.selectedRegionIds.size > 0 && this.cachedProcessedPolygon) {
        this.confirmRegionSelection(true);
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // Check if we have temporary prompts that form a valid loop - merge them
      if (this.canMergeTemporaryPrompts()) {
        this.mergeTemporaryShape();
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // If editing a room, confirm the changes
      if (this.editingShape && this.linePrompts.length >= 3) {
        this.exitShapeEditMode(true);
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // Otherwise create a new room from the shape
      if (this.linePrompts.length >= 3) {
        this.finalizeShape();
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    } else if ((event.key === 'r' || event.key === 'R') && !event.repeat) {
      // Refresh detection (ignore key repeat)
      this.detectLines();
    } else if (event.key === 'Shift') {
      this.shiftDown = true;
      this.updatePreviewLinesForShift();
      if (this.linePrompts.length > 0) {
        this.parentTool.pushHintOverride('shift_snap');
      }
      // Re-run drag calculation with rotation enabled
      if (this.promptSphereDrag && this.lastDragMouse) {
        this.movePromptToMouse(this.lastDragMouse);
      }
    } else if (event.key === 'Control' && !event.repeat) {
      // Refinement start is handled by LineTool before delegation
      this.ctrlDown = true;
      if (!this.parentTool.isRefining) {
        this.updatePreviewLinesForCtrl();
        if (this.linePrompts.length > 0) {
          this.parentTool.pushHintOverride('ctrl_no_snap');
        }
      }
      if (this.promptSphereDrag && this.lastDragMouse) {
        this.movePromptToMouse(this.lastDragMouse);
      }
    }
  }

  /**
   * Handle key up events
   */
  onKeyUp(event: KeyboardEvent): void {
    if (this.edgeOpController.isActive()) {
      this.edgeOpController.onKeyUp(event);
    }
    if (event.key === 'Shift') {
      this.shiftDown = false;
      // End the brush axis lock on the KEY event, not the next mousemove — a
      // quick Shift re-tap with the pointer at rest would otherwise never be
      // observed, and the re-hold couldn't rotate the lock 90°.
      if (this.regionBrushManager.regionDragging) this.brushAxisLock = null;
      this.updatePreviewLinesForShift();
      this.parentTool.popHintOverride();
      // Re-run drag calculation without rotation
      if (this.promptSphereDrag && this.lastDragMouse) {
        this.movePromptToMouse(this.lastDragMouse);
      }
    } else if (event.key === 'Control') {
      // Refinement finalization is handled by LineTool before delegation
      this.ctrlDown = false;
      if (!this.parentTool.isRefining) {
        this.updatePreviewLinesForCtrl();
        this.parentTool.popHintOverride();
      }
      if (this.promptSphereDrag && this.lastDragMouse) {
        this.movePromptToMouse(this.lastDragMouse);
      }
    }
  }

  /**
   * Update preview lines visibility based on shift state.
   * When prompts exist: Shift toggles perpendicular snap mode.
   * When no prompts: Shift hides preview lines (manual drawing mode).
   */
  private updatePreviewLinesForShift(): void {
    // When prompts exist: Shift enables perpendicular snap mode
    if (this.linePrompts.length > 0) {
      if (this.shiftDown) {
        for (const line of this.detectedLines) {
          line.isHovered = false;
          line.isHidden = true;
        }
        this.visualizer.hideIsolationPlane();
        this.renderDetectedLinesSafe();
      } else {
        this.perpendicularSnapTarget = null;
        this.shiftLockedPromptId = null;
        this.visualizer.clearLinePreview();
        // Show existing lines again
        for (const line of this.detectedLines) {
          line.isHidden = false;
        }
        this.renderDetectedLinesSafe();
        // Re-render prompts without shift highlight
        this.visualizer.updatePromptVisuals(this.linePrompts, this.hoveredPromptId);
      }
      return;
    }

    // No prompts: original shift behavior (hide preview lines for manual drawing)
    // If we're in two-click mode (first point placed), stay in manual mode regardless of shift state
    if (this.firstClickPoint) {
      // Keep lines hidden while in two-click mode
      for (const line of this.detectedLines) {
        line.isHovered = false;
        line.isHidden = true;
      }
      this.lineRenderer.render(this.detectedLines);
      return;
    }

    // If we're in drag mode (drag start point set), stay in manual mode regardless of shift state
    if (this.dragStartWorldPoint) {
      // Keep lines hidden while in drag mode
      for (const line of this.detectedLines) {
        line.isHovered = false;
        line.isHidden = true;
      }
      this.lineRenderer.render(this.detectedLines);
      return;
    }

    if (this.shiftDown) {
      // Shift pressed: hide all preview lines for manual drawing mode
      for (const line of this.detectedLines) {
        line.isHovered = false;
        line.isHidden = true;
      }
      // Hide isolation plane when entering manual mode
      this.visualizer.hideIsolationPlane();
    } else {
      // Shift released: show all preview lines again
      for (const line of this.detectedLines) {
        line.isHidden = false;
        // isHovered will be updated on next mouse move
      }
    }

    this.lineRenderer.render(this.detectedLines);
  }

  /**
   * Update preview lines visibility based on control key state.
   * When control is held: show existing detected lines (no detection triggered)
   * When control is released: just hide lines (no segmentation triggered - that only happens on camera move)
   */
  private updatePreviewLinesForCtrl(): void {
    if (this.linePrompts.length === 0) {
      // No prompts: Ctrl toggles between line mode and region preview mode
      if (this.ctrlDown) {
        // Switching to line mode - show existing lines (they render in world space)
        for (const line of this.detectedLines) {
          line.isHidden = false;
        }
        this.renderDetectedLinesSafe();
        // Note: We no longer clear polygonRenderer on Ctrl press since it renders in world space
        // Just clear region polygon preview state and brush cursor
        this.clearRegionPolygonPreview();
        this.clearBrushCursor();
      } else {
        // Switching to region mode - hide lines
        for (const line of this.detectedLines) {
          line.isHovered = false;
          line.isHidden = true;
        }
        this.renderDetectedLinesSafe();
        // Run segmentation if it was blocked while Ctrl was held during a camera move
        if (!this.cameraMoving && !this.isMouseOverRoom()) {
          this.runSegmentation();
        }
      }
    }
    // When prompts exist, Ctrl has no perpendicular snap effect (that's now Shift)
  }

  /**
   * Update the perpendicular snap preview during mouse move when shift is held.
   * Draws a preview line from the cursor to the closest point on a prompt line.
   */
  private updatePerpendicularSnapPreview(mouse: THREE.Vector2): void {
    // Get world position from mouse
    const worldPoint = this.coordinateTransformer.screenToWorld(mouse);
    if (!worldPoint) {
      this.perpendicularSnapTarget = null;
      this.visualizer.clearLinePreview();
      return;
    }

    // Check if hovering over a prompt sphere or line — lock it as snap target
    const hoveredPromptSphere = this.hitTester.findHoveredPromptSphere(mouse, this.linePrompts);
    if (hoveredPromptSphere) {
      this.shiftLockedPromptId = hoveredPromptSphere.id;
    } else {
      const hoveredPromptLine = this.hitTester.findPromptLineAtPosition(mouse, this.linePrompts);
      if (hoveredPromptLine) {
        this.shiftLockedPromptId = hoveredPromptLine.prompt.id;
      }
    }

    // Determine which prompt to snap to: locked prompt or closest
    const lockedPrompt = this.shiftLockedPromptId ? this.linePrompts.find((p) => p.id === this.shiftLockedPromptId) : undefined;

    const snapTarget = this.hitTester.findPerpendicularSnapTarget(worldPoint, this.linePrompts, lockedPrompt);
    if (!snapTarget) {
      this.perpendicularSnapTarget = null;
      this.visualizer.clearLinePreview();
      return;
    }

    // Store the snap target for click handling
    this.perpendicularSnapTarget = {
      prompt: snapTarget.prompt,
      snapPoint: snapTarget.snapPoint,
      previewEndPoint: worldPoint,
      distance: snapTarget.distance,
    };

    // Set Z height of snap point to match
    this.perpendicularSnapTarget.snapPoint.z = worldPoint.z;

    // Draw the preview line from cursor to snap point
    this.drawPerpendicularPreviewLine(worldPoint, this.perpendicularSnapTarget.snapPoint);

    // Update prompt visuals with shift-highlight on the snap target prompt
    this.visualizer.updatePromptVisuals(this.linePrompts, this.hoveredPromptId, null, this.shiftLockedPromptId);
  }

  /**
   * Draw a preview line for perpendicular snap (from cursor to snap point on prompt line)
   */
  private drawPerpendicularPreviewLine(fromPoint: THREE.Vector3, toPoint: THREE.Vector3): void {
    this.visualizer.renderLinePreview(fromPoint, toPoint);
  }

  /**
   * Get debug widget for line detection visualization
   */
  getDebugWidget(): LineDetectionDebugWidget | null {
    if (!this.debugWidget) {
      this.debugWidget = new LineDetectionDebugWidget();

      // Set up unit converters for displaying distances in cm
      this.debugWidget.setUnitConverters(
        (pixels: number) => this.coordinateTransformer.pixelsToCm(pixels),
        (cm: number) => this.coordinateTransformer.cmToPixels(cm),
      );

      // Set up line detection pipeline with the debug widget
      this.debugWidget.setPipeline(this.lineDetectionService.getPipeline(), () => {
        // Re-run line detection when pipeline changes
        this.detectLines();
      });

      // Set up depth segmentation pipeline tab
      this.debugWidget.setDepthPipeline(this.lineDetectionService.getDepthPipeline(), () => {
        // Sync changes to worker then re-run detection to apply new depth config
        this.lineDetectionService.updateDepthPipelineConfig().then(() => {
          this.detectLines();
        });
      });

      // Set up polygon processing pipeline tab
      this.debugWidget.setPolygonPipeline(this.lineDetectionService.getPolygonPipeline(), () => {
        // Sync changes to worker, clear cache, and reprocess current polygon
        this.lineDetectionService.updatePolygonPipelineConfig().then(() => {
          this.regionPolygonCache.clear();
          this.cachedProcessedPolygon = null;
          this.confirmedPolygonSnapshot = null;

          // Re-process the currently displayed polygon with the new pipeline
          if (this.lastPreviewRectResult && this.regionBrushManager.selectedRegionIds.size > 0) {
            const allIds = Array.from(this.regionBrushManager.selectedRegionIds).sort((a, b) => a - b);
            const cacheKey = `regions_${allIds.join('_')}`;
            this.triggerPolygonProcessing(this.lastPreviewRectResult.imageData, cacheKey);
          }
        });
      });

      // Set up callback for spawning debug textures in world
      this.debugWidget.setSpawnTextureCallback((type, imageData) => {
        this.spawnDebugTextureInWorld(type, imageData);
      });

      // Set up callback for clearing debug textures from world
      this.debugWidget.setClearTextureCallback((type) => {
        this.clearDebugTexturesFromWorld(type);
      });

      // Set up density snap debug toggle
      this.debugWidget.setDensitySnapDebugCallback((enabled) => {
        this.densitySnapper.debugEnabled = enabled;
        if (!enabled) {
          this.densitySnapper.clearDebug(this.viewer.scene.sceneDebug);
        }
      });

      // Set up callback for getting line prompts (copy to clipboard)
      this.debugWidget.setGetLinePromptsCallback(() => {
        return this.linePrompts.map((p) => ({
          id: p.id,
          worldPoint: { x: p.worldPoint.x, y: p.worldPoint.y, z: p.worldPoint.z },
          direction: { x: p.direction.x, y: p.direction.y, z: p.direction.z },
          positiveLength: p.positiveLength,
          negativeLength: p.negativeLength,
          isTemporary: p.isTemporary,
        }));
      });

      // Set up callback for setting line prompts (paste from clipboard)
      this.debugWidget.setSetLinePromptsCallback((prompts) => {
        this.parentTool.pushUndoSnapshot();
        this.linePrompts = prompts.map((p) => ({
          id: p.id,
          worldPoint: new THREE.Vector3(p.worldPoint.x, p.worldPoint.y, p.worldPoint.z),
          direction: new THREE.Vector3(p.direction.x, p.direction.y, p.direction.z),
          screenPosition: this.coordinateTransformer.worldToScreen(new THREE.Vector3(p.worldPoint.x, p.worldPoint.y, p.worldPoint.z)),
          positiveLength: p.positiveLength,
          negativeLength: p.negativeLength,
          isTemporary: p.isTemporary,
        }));
        this.updateShapePreview();
      });

      // Set up callback for getting processed polygon (copy to clipboard)
      this.debugWidget.setGetProcessedPolygonCallback(() => {
        return this.cachedProcessedPolygon;
      });
    }
    return this.debugWidget;
  }

  /**
   * Update debug widget with latest detection data
   */
  private updateDebugWidget(): void {
    if (!this.debugWidget) {
      return;
    }

    // Update position map visualization
    this.debugWidget.setPositionMap(this.lineDetectionService.cachedPositionMap);

    // Update density map visualization
    this.debugWidget.setDensityMap(this.lineDetectionService.cachedDensityMap);

    // Update statistics if detection data is available
    const debugData = this.lineDetectionService.lastDebugData;
    if (debugData) {
      this.debugWidget.setStats({
        detectionTime: debugData.detectionTime,
        linesDetected: debugData.rawLines.length,
        linesAfterFiltering: debugData.filteredLines.length,
        imageSize: {
          width: debugData.capturedImage.width,
          height: debugData.capturedImage.height,
        },
        config: {
          minConfidence: this.lineDetectionService['config'].minConfidence,
          minLineLength: this.lineDetectionService['config'].minLineLength,
          houghThreshold: this.lineDetectionService['config'].houghThreshold,
        },
        stepTimings: this.lastStepTimings,
        mapResolutions: this.lastMapResolutions,
      });
    } else if (Object.keys(this.lastStepTimings).length > 0 || Object.keys(this.lastMapResolutions).length > 0) {
      // Show step timings even without detection data (e.g., segmentation only)
      this.debugWidget.setStats({
        detectionTime: 0,
        linesDetected: 0,
        linesAfterFiltering: 0,
        imageSize: { width: 0, height: 0 },
        config: {
          minConfidence: this.lineDetectionService['config'].minConfidence,
          minLineLength: this.lineDetectionService['config'].minLineLength,
          houghThreshold: this.lineDetectionService['config'].houghThreshold,
        },
        stepTimings: this.lastStepTimings,
        mapResolutions: this.lastMapResolutions,
      });
    }

    // Update segmentation visualization
    this.debugWidget.setSegmentationData(this.lineDetectionService.lastSegmentationColoredData, this.lineDetectionService.lastSegmentationWidth, this.lineDetectionService.lastSegmentationHeight);

    // Refresh pipeline panel to update cm/pixel conversions
    this.debugWidget.refreshPipelinePanel();
  }

  /**
   * Spawn a debug texture as a plane in the 3D world.
   * Uses the current mask corners from the coordinate transformer for positioning.
   * The plane is positioned at the associated plane's Z level (room floor).
   */
  private spawnDebugTextureInWorld(type: 'position-map' | 'density-map' | 'segmentation', imageData: ImageData): void {
    const debugTexturePlanes = this.viewer.debugTexturePlanes;
    if (!debugTexturePlanes) {
      console.warn('[LineTool] Debug texture planes manager not available');
      return;
    }

    const maskCorners = this.coordinateTransformer.getMaskCorners();
    if (maskCorners.length !== 4) {
      console.warn('[LineTool] No mask corners available for spawning texture plane');
      return;
    }

    // Get the room's Z level from the associated plane (floor level)
    const associatedPlane = this.viewer.viewerMode.associatedPlane;
    let roomZ: number | null = null;
    if (associatedPlane) {
      roomZ = associatedPlane.position.z;
    }

    // Adjust corners to use room Z level if available
    let adjustedCorners = maskCorners;
    if (roomZ !== null) {
      const z = roomZ;
      adjustedCorners = maskCorners.map((corner) => {
        const adjusted = corner.clone();
        adjusted.z = z;
        return adjusted;
      });
    }

    // Spawn the texture plane at the adjusted corners
    const planeId = debugTexturePlanes.createPlaneFromImageData(imageData, adjustedCorners, {
      type: type,
      label: type.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      opacity: 0.8,
      zOffset: 0.02, // Slight offset to prevent z-fighting with point cloud
    });

    console.log(`[LineTool] Spawned debug texture plane: ${planeId}`);
  }

  /**
   * Clear all debug texture planes of a specific type from the 3D world.
   */
  private clearDebugTexturesFromWorld(type: 'position-map' | 'density-map' | 'segmentation'): void {
    const debugTexturePlanes = this.viewer.debugTexturePlanes;
    if (!debugTexturePlanes) {
      console.warn('[LineTool] Debug texture planes manager not available');
      return;
    }

    const count = debugTexturePlanes.clearPlanesByType(type);
    console.log(`[LineTool] Cleared ${count} debug texture plane(s) of type: ${type}`);
  }

  /**
   * Initialize point size override from first point cloud
   * Stores original point sizes and applies the persisted override value
   */
  private initializePointSizeOverride(): void {
    const pointclouds = this.viewer.scene.pointclouds;
    if (pointclouds.length === 0) return;

    // Only initialize the setting value from point cloud on first activation
    if (!this.pointSizeInitialized) {
      const firstPointCloud = pointclouds[0];
      if (firstPointCloud.material && firstPointCloud.material.uniforms.size) {
        this.settings.temporaryPointSize = Number(firstPointCloud.material.uniforms.size.value);
      }
      this.pointSizeInitialized = true;
    }

    // Store original point sizes for all point clouds (always do this on enable)
    for (const pointcloud of pointclouds) {
      if (pointcloud.material && pointcloud.material.uniforms.size) {
        this.originalPointSizes.set(pointcloud, Number(pointcloud.material.uniforms.size.value));
      }
    }

    // Apply the persisted override value
    this.applyPointSizeOverride(this.settings.temporaryPointSize);
  }

  /**
   * Apply temporary point size override to all point clouds
   */
  private applyPointSizeOverride(pointSize: number): void {
    const pointclouds = this.viewer.scene.pointclouds;
    for (const pointcloud of pointclouds) {
      if (pointcloud.material && pointcloud.material.uniforms.size) {
        pointcloud.material.uniforms.size.value = pointSize;
      }
    }
  }

  /**
   * Restore original point sizes when tool exits
   */
  private restoreOriginalPointSizes(): void {
    for (const [pointcloud, originalSize] of this.originalPointSizes) {
      if (pointcloud.material && pointcloud.material.uniforms.size) {
        pointcloud.material.uniforms.size.value = originalSize;
      }
    }
    this.originalPointSizes.clear();
  }

  /**
   * Handle settings changes from the settings panel
   * Called by the parent LineTool shell when settings change
   */
  onSettingsChange(event: ToolSettingsChangeEvent): void {
    const { key, value } = event;

    // Update settings
    if (key === 'temporaryPointSize') {
      this.settings.temporaryPointSize = value as number;
      this.applyPointSizeOverride(this.settings.temporaryPointSize);
    } else if (key === 'enableCustomSegmentIsolation') {
      this.settings.enableCustomSegmentIsolation = value as boolean;
    } else if (key === 'segmentIsolationTolerance') {
      this.settings.segmentIsolationTolerance = value as number;
    } else if (key === 'segmentIsolationHeight') {
      this.settings.segmentIsolationHeight = value as number;
    }

    // Note: Floor isolation is handled by the LineTool shell
    // The shell will re-apply isolation and pass the updated plane to us
  }

  /**
   * Get the current line prompts (for mode switching)
   */
  getLinePrompts(): LinePrompt[] {
    return this.linePrompts;
  }

  /**
   * Clear the detected-lines rendering (green suggestion lines) without
   * deactivating the mode. Hosts that only borrow this tool for room-edit
   * sessions (Wall Tracer) call this when a session ends — the suggestions
   * must not linger once another tool owns the canvas again.
   */
  clearDetectionVisuals(): void {
    this.lineRenderer.clear();
    this.detectedLines = [];
    this.sparkleParticles.stop();
  }

  /**
   * Public detection refresh (same as the 'r' shortcut). For kinds with
   * detection disabled but `captureMapsForSnapping` set, this recaptures
   * just the snap maps — hosts call it when they inject prompts without
   * going through enterShapeEditMode (Wall Tracer draft/test sessions).
   */
  refreshDetection(): void {
    void this.detectLines();
  }

  /**
   * Set line prompts (for mode switching)
   * Also updates the shape preview and visuals
   */
  setLinePrompts(prompts: LinePrompt[]): void {
    this.linePrompts = prompts.map((p) => ({
      id: p.id,
      worldPoint: p.worldPoint.clone(),
      direction: p.direction.clone(),
      screenPosition: p.screenPosition.clone(),
      positiveLength: p.positiveLength,
      negativeLength: p.negativeLength,
      connections: p.connections,
    }));

    // Update nextPromptId to be higher than any existing prompt
    for (const prompt of this.linePrompts) {
      const match = prompt.id.match(/prompt_(\d+)/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num >= this.nextPromptId) {
          this.nextPromptId = num + 1;
        }
      }
    }

    // Update screen positions and shape preview
    this.updatePromptScreenPositions();
    if (this.linePrompts.length >= 3) {
      this.updateShapePreview();
    }
    this.visualizer.updatePromptVisuals(this.linePrompts, this.hoveredPromptId);
  }

  /**
   * Cleanup
   */
  dispose(): void {
    // Cancel any ongoing detection
    if (this.detectionAbortController) {
      this.detectionAbortController.abort();
      this.detectionAbortController = null;
    }

    this.lineDetectionService.dispose();
    this.lineRenderer.dispose();
    this.areaSelector.dispose();
    this.visualizer.dispose();
    this.polygonRenderer.dispose();
    this.sparkleParticles.dispose();
    this.clearAutoPromptVisuals();
    this.regionBrushManager.dispose();
    this.shapeRendererModule.clear();
    if (this.autoPromptSpheresGroup) {
      this.planePreviewScene.remove(this.autoPromptSpheresGroup);
      this.autoPromptSpheresGroup = null;
    }
  }
}
